import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { dedupById } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import { useRbac } from "@/lib/rbac";
import { toast } from "sonner";
import Barcode from "react-barcode";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlusIcon, TrashIcon, PencilIcon, PrinterIcon, EyeIcon, BarcodeIcon, CopyIcon, MagnifyingGlassIcon, XIcon, LinkSimpleIcon, LinkBreakIcon, StackIcon, MagicWandIcon, LockIcon } from "@phosphor-icons/react";
import { fmtDateTimeIST } from "@/lib/datetime";
import DocumentInfo from "@/components/DocumentInfo";
import { RowActionsMenu } from "@/components/RowActionsMenu";

import FormattedNumberInput from "@/components/FormattedNumberInput";
// Feb 2026 · Central UoM formatters — keeps the packing-slip sticker
// print + on-screen totals in the same number language as invoices /
// DCs / SO prints (MTR whole + Indian commas, KG 3 dp Indian).
import { fmtQty } from "@/lib/uom";
import { OrderRef, StockBadge, fulfilmentOf, isStockOrder, stockLinesOf } from "@/components/StockBadge";
import { StockPackPicker } from "@/components/StockPackPicker";

// SOESO-FULFIL-01 · register tags: Customer slip Created from FG Stock (+ source Stock refs) / Stock slip allocation.
function FgSlipTags({ s }) {
  if (s.created_from_fg) {
    return (
      <div className="mt-1 flex flex-col gap-0.5" data-testid={`ps-fg-origin-${s.slip_no}`}>
        <span className="px-1.5 py-0.5 rounded-sm bg-violet-500/15 text-violet-300 text-[9px] font-semibold uppercase tracking-wider w-fit">Created from FG Stock</span>
        <span className="text-[10px] text-zinc-500 inline-flex flex-wrap items-center gap-1">
          src {(s.fg_source_stock_orders || []).map(n => <span key={n} className="inline-flex items-center gap-1">{n}<span>·</span><StockBadge /></span>)}
          <span>· {(s.fg_source_slips || []).join(", ")}</span>
        </span>
      </div>
    );
  }
  if (s.fg_allocation_status) {
    return <div className="mt-1 text-[9px] uppercase tracking-wider text-amber-300" data-testid={`ps-fg-alloc-${s.slip_no}`}>{s.fg_allocation_status}</div>;
  }
  return null;
}
const inp = "w-full h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]";
const inpNum = inp + " text-right font-mono";
const selCls = "bg-[var(--bg-0)] border-[var(--border)] h-9 text-sm";

const emptyBox = () => ({ product_code: "", item_id: "", meters: 0, joints: 0, net_weight: 0, tare_weight: 0, gross_weight: 0 });

// Aug 2026 · Searchable Customer combobox (user ask). Replaces the
// standard Radix Select with a native combobox pattern — matches by
// customer NAME or CODE, live-filtered, mouse-friendly. Same instance
// is reused for the SO Customer and ESO Buyer slots so behaviour is
// identical in both places.
function SearchableCustomerPicker({ testIdPrefix, open, setOpen, query, setQuery, visible, total, picked, onPick }) {
  return (
    <div className="relative" data-nouppercase>
      <button
        type="button"
        data-testid={testIdPrefix}
        onClick={() => setOpen(v => !v)}
        className="w-full h-9 px-2.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm text-left flex items-center justify-between focus:outline-none focus:border-[var(--accent)]"
      >
        <span className={picked ? "text-white truncate" : "text-zinc-500"}>
          {picked ? (
            <>
              <span>{picked.name}</span>
              {picked.code ? <span className="text-zinc-500 font-mono ml-2 text-[10px]"> · {picked.code}</span> : null}
            </>
          ) : `Pick a Customer… (${total} total)`}
        </span>
        <span className="text-zinc-500 text-xs">▾</span>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-[var(--bg-1)] border border-[var(--border)] rounded-sm shadow-xl">
          <div className="p-2 border-b border-[var(--border)]">
            <input
              autoFocus
              data-testid={`${testIdPrefix}-search`}
              data-nouppercase
              value={query}
              onChange={e => setQuery(e.target.value)}
              className="w-full h-9 px-2.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]"
              placeholder="Search by name or customer code…"
            />
            <div className="text-[10px] text-zinc-500 mt-1">
              {visible.length} of {total} shown
            </div>
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {visible.length === 0 && (
              <div className="px-3 py-4 text-xs text-zinc-500 text-center">No customers match "{query}"</div>
            )}
            {visible.map(c => (
              <button
                type="button"
                key={c.id}
                data-testid={`${testIdPrefix}-opt-${c.id}`}
                onClick={() => { onPick(c); setOpen(false); setQuery(""); }}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--bg-2)] flex items-center justify-between gap-3"
              >
                <span className="text-white truncate">{c.name}</span>
                {c.code && <span className="text-zinc-500 text-[10px] font-mono shrink-0">{c.code}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Label size presets — all use the TSC TE244 (203 DPI) driver, only the
// physical @page geometry + sticker layout changes at runtime.
//   • 4x6    → shipping: FRONT top-3in / BACK bottom-3in (paired stickers)
//   • 4x4    → general:  FRONT top-2in / BACK bottom-2in (paired stickers)
//   • 4x2    → barcode-only: single sticker · box no + barcode (dispatch tags)
//   • 2x1    → mini spool: box no + tiny barcode (spool identification)
const LABEL_PRESETS = {
  "4x6": {
    label: '4×6" Shipping',
    pageW: "4in", pageH: "6in",
    paired: true, stickerH: "3in",
    boxFont: "24pt", rowFont: "11pt", hdrFont: "10pt",
    bcW: "3.4in", bcH: "0.65in", bcSvgHeight: 50, bcTextSize: 14,
    padding: "0.12in 0.18in 0.10in 0.18in",
    showAll: true,
  },
  "4x4": {
    label: '4×4" General',
    pageW: "4in", pageH: "4in",
    paired: true, stickerH: "2in",
    boxFont: "20pt", rowFont: "10pt", hdrFont: "9pt",
    bcW: "3.4in", bcH: "0.55in", bcSvgHeight: 40, bcTextSize: 12,
    padding: "0.08in 0.15in 0.06in 0.15in",
    showAll: true,
  },
  "4x2": {
    label: '4×2" Barcode-Only',
    pageW: "4in", pageH: "2in",
    paired: false, stickerH: "2in",
    boxFont: "20pt", rowFont: "10pt", hdrFont: "8.5pt",
    bcW: "3.6in", bcH: "0.8in", bcSvgHeight: 60, bcTextSize: 13,
    padding: "0.08in 0.15in",
    showAll: false, // box no + product + barcode only
  },
  // 100 × 75 mm dispatch label — full-detail single sticker sized for the
  // common Indian textile packaging carton. Uses mm units end-to-end so the
  // TSC TE244 driver honours the physical media exactly.
  "100x75": {
    label: "100×75 mm Dispatch",
    pageW: "100mm", pageH: "75mm",
    paired: false, stickerH: "75mm",
    boxFont: "22pt", rowFont: "10.5pt", hdrFont: "9pt",
    bcW: "88mm", bcH: "18mm", bcSvgHeight: 55, bcTextSize: 13,
    padding: "3mm 4mm 2.5mm 4mm",
    showAll: true,
  },
  "2x1": {
    label: '2×1" Mini Spool',
    pageW: "2in", pageH: "1in",
    paired: false, stickerH: "1in",
    boxFont: "11pt", rowFont: "7pt", hdrFont: "6pt",
    bcW: "1.85in", bcH: "0.45in", bcSvgHeight: 30, bcTextSize: 8,
    padding: "0.04in 0.06in",
    showAll: false, // barcode + box no only
  },
};

export default function PackingSlipsTab({ onNextStep, mode }) {
  // Mar 2026 · Mode-aware split. `mode="export"` locks the module to
  // ESO / Excess-vs-ESO sourcing (mints EPS-YY-YY-NNNNN via backend).
  // `mode="domestic"` (or unset — legacy /packing-slips route) hides
  // ESO tabs and keeps the classic PS-YY-NNN behaviour. Both modes
  // share the SAME UI, columns, action buttons, single-entry dialog,
  // bulk grid and barcode printer — muscle memory preserved.
  const isExportMode = mode === "export";
  const isDomesticMode = mode === "domestic";
  const { hasPerm } = useAuth();
  // Aug 31, 2026 · Reactive perm gate — `useRbac().can()` re-renders as
  // soon as `/rbac/me/permissions` resolves post-login. `ready` flag
  // keeps Create buttons DISABLED (not hidden) during the initial
  // fetch so the layout doesn't jump.
  const { can: rbacCan, ready: rbacReady } = useRbac();
  // RR02 · B-5 · action perms follow the backend's source-aware module.
  const slipModule = isExportMode ? "exports_slips" : "sales_orders_slips";
  const canCreateSlip = rbacCan(slipModule, "create");
  // Aug 31, 2026 · Reactive Delete/Un-Allocate gate — mirrors canCreateSlip
  // but keyed on the `delete` action. Un-Allocate is a destructive reversal
  // (deletes child slips + restores SO pending) so it lives under the same
  // grant as hard-delete.
  const canDeleteSlip = rbacCan(slipModule, "delete");
  // Sep 10, 2026 - the Edit control was ungated entirely; a view-only role
  // could open the editor and only hit 403 on save.
  const canEditSlip = rbacCan(slipModule, "edit");
  const [slips, setSlips] = useState([]);
  // Mar 2026 · Truncation Fix — client-side pagination. Renders first
  // PS_PAGE_SIZE rows initially; "Show More" button appends the next
  // page (or "Show All" flips to the full list). Reset on search change.
  const PS_PAGE_SIZE = 100;
  const [visibleCount, setVisibleCount] = useState(PS_PAGE_SIZE);
  // Feb 2026 · Set of Packing Slip IDs that are packed into a PL. Populated
  // from GET /packing-lists/ at load-time. Downstream-locked slips can't be
  // edited, deleted, or unlinked from their SO/ESO — only amended.
  const [lockedPsIds, setLockedPsIds] = useState(() => new Set());
  const [lockedPsMap, setLockedPsMap] = useState(() => ({}));  // ps_id → pl_no
  const [orders, setOrders] = useState([]);
  // Export Sales Orders (Feb 2026) — Packing Slips can be packed against an ESO
  // in addition to a domestic Sales Order. Loaded alongside the SO roster so
  // the source-type toggle can offer both without a second network round-trip.
  const [exportOrders, setExportOrders] = useState([]);
  const [items, setItems] = useState([]);
  // Article Master (Feb 2026 · bulk-generator style filter) — loaded so the
  // Stock-mode Article dropdown can narrow to just the product codes whose
  // `style_no` matches whatever the operator typed. Items master doesn't
  // carry `style_no`, articles do.
  const [articles, setArticles] = useState([]);
  // Customer roster (Feb 2026) — pulled once so the thermal label can print
  // the buyer's name next to SO/PO. Keyed by id via `customerNameFor`.
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  // Persist the operator's last-used label preset across sessions so they
  // don't have to re-select their printer size on every print. Falls back
  // to the default 4×6 if sessionStorage is unavailable or empty.
  const [previewLabelOpen, setPreviewLabelOpen] = useState(false);
  // Per-slip preset override (Feb 2026). Null = use the global `labelPreset`;
  // when set from inside the View dialog, only that slip's on-screen render +
  // print emit at the override size. Auto-clears whenever a different slip is
  // opened so the override never leaks across slips.
  const [viewPreset, setViewPreset] = useState(null);
  const [labelPreset, setLabelPreset] = useState(() => {
    try {
      const saved = sessionStorage.getItem("mes_ps_label_preset");
      if (saved && LABEL_PRESETS[saved]) return saved;
    } catch (e) { /* noop */ }
    return "4x6";
  });
  useEffect(() => {
    try { sessionStorage.setItem("mes_ps_label_preset", labelPreset); } catch (e) { /* noop */ }
  }, [labelPreset]);
  // Packing Slip source (Feb 2026): a slip is either packed AGAINST a Sales
  // Order (SO-bound → line items constrain the box's Product Code, workflow
  // nudge fires) or AGAINST STOCK (loose inventory, no SO — full Item Master
  // is available as Product Code source, order_id stays empty).
  const [sourceType, setSourceType] = useState(isExportMode ? "export_order" : "sales_order");
  const [form, setForm] = useState({ order_id: "", export_order_id: "", excess_entry_id: "", pack_date: new Date().toISOString().slice(0, 10), remarks: "", boxes: [emptyBox()] });

  // Bulk Packing Slip generator (Feb 2026). Warehouses often ship the same
  // article to the same customer in N identically-packed boxes; keying each
  // one manually was 5 clicks × N. This state powers a dedicated dialog that
  // creates N slips (one box each, matching the existing schema) in a single
  // POST → /api/packing-slips/bulk. Customer picker filters the SO dropdown
  // so the operator picks the buyer first, then the SO, then the article.
  const [bulkOpen, setBulkOpen] = useState(false);
  // Feb 27, 2026 · Approved-for-Billing excess lots for the Excess tabs.
  const [approvedLots, setApprovedLots] = useState([]);
  const [approvedLotsLoading, setApprovedLotsLoading] = useState(false);
  // Feb 28, 2026 · Pending-Approval count so the empty state can guide the
  // operator: "3 pending — approve them to dispatch here".
  const [pendingExcessCount, setPendingExcessCount] = useState(0);
  const [bulkSaving, setBulkSaving] = useState(false);
  // Mar 2026 · Inline field-level error state. Keys use dotted paths so both
  // the single-slip form (`form.pack_date`, `form.boxes.<i>.meters` …) and
  // the bulk form (`bulk.pack_date`, `bulk.box.<i>.meters` …) share the same
  // map — helpers below produce a red-border className when the input's key
  // is present.
  const [errors, setErrors] = useState({});
  const clearErrors = () => setErrors({});
  const errClass = (k) => errors[k] ? " border-red-500 ring-1 ring-red-500/50 bg-red-500/5" : "";
  const markErr = (k, msg) => {
    setErrors({ [k]: true });
    toast.error(msg);
    // Auto-scroll first offending row into view so long forms surface it.
    try {
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-err-key="${k}"]`);
        if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    } catch (_) { /* jsdom / older browser */ }
    return false;
  };
  const emptyBulkBox = () => ({ meters: 0, joints: 0, net_weight: 0, tare_weight: 0 });
  const emptyBulk = () => ({
    customer_id: "",
    order_id: "",
    export_order_id: "",
    source_type: isExportMode ? "export_order" : "sales_order",
    product_code: "",
    item_id: "",
    style_no: "",
    pack_date: new Date().toISOString().slice(0, 10),
    remarks: "",
    // Feb 27, 2026 · Set when the operator picks an approved excess lot.
    excess_entry_id: "",
    // Feb 2026 · was `num_boxes` + single profile. Now one line item per box.
    // Each row becomes one Packing Slip (one box inside). Default 1 line so
    // the dialog isn't empty on open.
    boxes: [emptyBulkBox()],
  });
  const [bulkForm, setBulkForm] = useState(emptyBulk);

  // Feb 27, 2026 · Fetch Approved-for-Billing excess lots when the
  // user opens either Excess tab (single or bulk). Scoped to the
  // selected order when one is picked so the picker stays relevant.
  useEffect(() => {
    const st = sourceType === "excess_so" || sourceType === "excess_eso" ? sourceType
            : (bulkOpen && (bulkForm.source_type === "excess_so" || bulkForm.source_type === "excess_eso")) ? bulkForm.source_type
            : "";
    if (!st) { setApprovedLots([]); setPendingExcessCount(0); return; }
    const wantSO = st === "excess_so";
    const orderId = wantSO ? (form.order_id || bulkForm.order_id || "")
                            : (form.export_order_id || bulkForm.export_order_id || "");
    setApprovedLotsLoading(true);
    // Fire two parallel requests — approved lots (source of the picker) +
    // the summary tile so the empty state can surface Pending counts.
    Promise.all([
      api.get("/excess-production/approved-lots", { params: { source_type: wantSO ? "SO" : "ESO", order_id: orderId } }),
      api.get("/excess-production/summary").catch(() => ({ data: { totals: { pending_entries: 0 } } })),
    ])
      .then(([lots, sum]) => {
        setApprovedLots(lots.data || []);
        setPendingExcessCount(Number(sum.data?.totals?.pending_entries || 0));
      })
      .catch(() => { setApprovedLots([]); setPendingExcessCount(0); })
      .finally(() => setApprovedLotsLoading(false));
    // eslint-disable-next-line
  }, [sourceType, bulkForm.source_type, bulkOpen, form.order_id, form.export_order_id, bulkForm.order_id, bulkForm.export_order_id]);
  const bulkAutoAllocate = async () => {
    if (!confirm("Run Auto Allocate against every unallocated stock slip? Child slips will be created for each SO match.")) return;
    try {
      const { data } = await api.post("/packing-slips/auto-allocate-all");
      toast.success(`Processed ${data.processed} slips · ${data.fully} fully · ${data.partial} partial · ${data.no_match} no-match · ${data.skipped} skipped · ${data.total_allocated}m allocated`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk Auto Allocate failed"); }
  };
  const unAllocate = async (slip) => {
    if (!rbacReady) return;
    if (!canDeleteSlip) { toast.error(`🔒 ${slipModule}.delete — ask an admin to grant this`); return; }
    if (!confirm(`Un-allocate ${slip.slip_no}? This deletes every child slip and restores SO pending balances. Cannot be undone.`)) return;
    try {
      const { data } = await api.post(`/packing-slips/${slip.id}/un-allocate`);
      toast.success(`${data.slip_no} reversed · ${data.reversed_children} children deleted · ${data.reversed_sos} SO(s) restored`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Un-Allocate failed"); }
  };
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const openHistory = async () => {
    try {
      const { data } = await api.get("/packing-slip-allocations");
      setHistoryRows(data.rows || []);
      setHistoryOpen(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load history"); }
  };

  const openBulk = () => { setBulkForm(emptyBulk()); setBulkOpen(true); };
  // Orders filtered by picked Customer (empty customer → all orders).
  const bulkOrdersForCustomer = useMemo(() => {
    const ok = orders.filter(o => fulfilmentOf(o) !== "sales_only");  // SOESO-FULFIL-01 · Sales Only = FG only
    if (!bulkForm.customer_id) return ok;
    return ok.filter(o => o.customer_id === bulkForm.customer_id);
  }, [orders, bulkForm.customer_id]);
  // Same filter for Export Sales Orders (ESO carries buyer_customer_id).
  const bulkExportOrdersForCustomer = useMemo(() => {
    const ok = exportOrders.filter(o => fulfilmentOf(o) !== "sales_only");
    if (!bulkForm.customer_id) return ok;
    return ok.filter(o => o.buyer_customer_id === bulkForm.customer_id);
  }, [exportOrders, bulkForm.customer_id]);
  // Article/Product-Code options for the Bulk dialog:
  //  - stock mode → Article Master (carries `style_no`, keyed by `product_code`)
  //  - SO mode with picked SO → its line items only
  //  - SO mode without SO → fall back to Article Master so the operator can
  //    still bulk-pack against loose stock even from within the SO source
  const bulkAllowedItems = useMemo(() => {
    const fromArticles = () => articles.map(a => ({
      id: a.id,
      code: a.product_code || a.article_no || a.code || "",
      style_no: a.style_no || "",
    })).filter(o => o.code);
    if (bulkForm.source_type === "stock") {  // STOCK-PACK-01 · only the selected Stock SO/ESO line
      const so = (isExportMode ? exportOrders : orders).find(o => o.id === bulkForm.stock_order_id);
      const l = stockLinesOf(so, isExportMode).find(x => x.line_id === bulkForm.stock_line_id);
      return l ? [{ id: l.item_id || l.code, code: l.code, style_no: l.style_no }] : [];
    }
    if (bulkForm.source_type === "export_order" || bulkForm.source_type === "excess_eso") {
      if (!bulkForm.export_order_id) return fromArticles();
      const eso = exportOrders.find(o => o.id === bulkForm.export_order_id);
      if (!eso) return [];
      const seen = new Set();
      return (eso.items || []).filter(li => li.article_no).map(li => ({
        id: li.article_no, code: li.article_no, style_no: li.style_no || "",
      })).filter(o => { if (seen.has(o.id)) return false; seen.add(o.id); return true; });
    }
    // sales_order + excess_so — narrow to picked SO's line items when set.
    if (!bulkForm.order_id) return fromArticles();
    const order = orders.find(o => o.id === bulkForm.order_id);
    if (!order) return [];
    const seen = new Set();
    return (order.items || []).filter(li => li.article_no).map(li => {
      const id = li.article_id || li.item_id || li.article_no;
      return { id, code: li.article_no, style_no: li.style_no || "" };
    }).filter(o => { if (seen.has(o.id)) return false; seen.add(o.id); return true; });
  }, [bulkForm.source_type, bulkForm.order_id, bulkForm.export_order_id, bulkForm.stock_order_id, bulkForm.stock_line_id, isExportMode, orders, exportOrders, articles]);

  // Style-driven filter (Feb 2026 user ask): when the operator types a Style
  // Number in the bulk dialog, the Article/Product Code dropdown narrows to
  // just the rows whose `style_no` contains the typed substring (case-
  // insensitive). Empty style → show every article. Kept as a separate memo
  // from `bulkAllowedItems` so the base source list re-uses cleanly.
  const bulkFilteredItems = useMemo(() => {
    const q = (bulkForm.style_no || "").trim().toLowerCase();
    if (!q) return bulkAllowedItems;
    return bulkAllowedItems.filter(it => (it.style_no || "").toLowerCase().includes(q));
  }, [bulkAllowedItems, bulkForm.style_no]);

  // Searchable style-picker (Feb 2026 · user ask): a distinct list of every
  // `style_no` seen across the operator's source (SO lines or full Article
  // Master), sorted, matched by the typed query. Powers the typeahead
  // dropdown under the Style Number input.
  const bulkAllStyles = useMemo(() => {
    const seen = new Set();
    bulkAllowedItems.forEach(it => { if (it.style_no) seen.add(it.style_no); });
    return Array.from(seen).sort();
  }, [bulkAllowedItems]);
  const bulkStyleSuggestions = useMemo(() => {
    const q = (bulkForm.style_no || "").trim().toLowerCase();
    if (!q) return bulkAllStyles.slice(0, 25);
    return bulkAllStyles.filter(s => s.toLowerCase().includes(q)).slice(0, 25);
  }, [bulkAllStyles, bulkForm.style_no]);
  const [bulkStyleOpen, setBulkStyleOpen] = useState(false);

  // Product-code search box that lives INSIDE the Product dropdown so the
  // operator can narrow further even after the style filter has run.
  const [bulkProductOpen, setBulkProductOpen] = useState(false);
  const [bulkProductQuery, setBulkProductQuery] = useState("");
  const bulkProductVisible = useMemo(() => {
    const q = bulkProductQuery.trim().toLowerCase();
    if (!q) return bulkFilteredItems;
    return bulkFilteredItems.filter(it =>
      (it.code || "").toLowerCase().includes(q) ||
      (it.style_no || "").toLowerCase().includes(q)
    );
  }, [bulkFilteredItems, bulkProductQuery]);

  // Aug 2026 · Searchable Customer combobox (user ask). Native input +
  // filtered list; matches by name OR customer code (case-insensitive).
  // Same picker instance is reused for the SO + ESO buyer slots below.
  const [bulkCustomerOpen, setBulkCustomerOpen] = useState(false);
  const [bulkCustomerQuery, setBulkCustomerQuery] = useState("");
  const bulkCustomerVisible = useMemo(() => {
    const q = bulkCustomerQuery.trim().toLowerCase();
    if (!q) return customers.slice(0, 200);
    return customers.filter(c =>
      (c.name || "").toLowerCase().includes(q) ||
      (c.code || "").toLowerCase().includes(q)
    ).slice(0, 200);
  }, [customers, bulkCustomerQuery]);
  const pickedCustomer = useMemo(
    () => customers.find(c => c.id === bulkForm.customer_id),
    [customers, bulkForm.customer_id]
  );

  // Aug 2026 · Real-time Pending Balance. When the operator picks
  // (order_id | export_order_id) + product_code, hit the backend for
  // {order_qty, packed_qty, pending_qty}. Result renders as a badge
  // next to the Product Code picker AND drives per-row red validation.
  const [pendingBalance, setPendingBalance] = useState({
    order_qty: 0, packed_qty: 0, pending_qty: 0, unit: "MTR", loading: false,
  });
  useEffect(() => {
    const pc = bulkForm.product_code;
    const oid = bulkForm.order_id;
    const eid = bulkForm.export_order_id;
    if (!pc || (!oid && !eid)) {
      setPendingBalance({ order_qty: 0, packed_qty: 0, pending_qty: 0, unit: "MTR", loading: false });
      return;
    }
    setPendingBalance(pb => ({ ...pb, loading: true }));
    (async () => {
      try {
        const { data } = await api.get("/packing-slips/pending-balance", {
          params: { order_id: oid || "", export_order_id: eid || "", product_code: pc },
        });
        setPendingBalance({ ...data, loading: false });
      } catch {
        setPendingBalance({ order_qty: 0, packed_qty: 0, pending_qty: 0, unit: "MTR", loading: false });
      }
    })();
  }, [bulkForm.product_code, bulkForm.order_id, bulkForm.export_order_id]);

  // Feb 2026 · totals across every box profile in the bulk grid.
  const bulkTotals = useMemo(() => {
    const rows = bulkForm.boxes || [];
    const acc = { meters: 0, joints: 0, net: 0, tare: 0, gross: 0 };
    for (const r of rows) {
      const n = Number(r.net_weight) || 0;
      const t = Number(r.tare_weight) || 0;
      acc.meters += Number(r.meters) || 0;
      acc.joints += Number(r.joints) || 0;
      acc.net += n;
      acc.tare += t;
      acc.gross += n + t;
    }
    return acc;
  }, [bulkForm.boxes]);
  const addBulkBox = () => setBulkForm(f => ({ ...f, boxes: [...(f.boxes || []), emptyBulkBox()] }));
  const rmBulkBox = (i) => setBulkForm(f => ({ ...f, boxes: (f.boxes || []).filter((_, x) => x !== i) }));
  const setBulkBox = (i, k, v) => setBulkForm(f => {
    let val = v;
    // Feb 27, 2026 · Cap each box's meters at the picked excess lot's
    // remaining capacity, minus meters already spread across sibling
    // boxes in the grid. Keeps bulk dispatch strictly within approval.
    if (k === "meters" && (f.source_type === "excess_so" || f.source_type === "excess_eso")) {
      const lot = approvedLots.find(l => l.id === f.excess_entry_id);
      if (lot) {
        const cap = Number(lot.excess_qty) || 0;
        const otherSum = (f.boxes || []).reduce((s, r, x) => s + (x === i ? 0 : (Number(r.meters) || 0)), 0);
        const remaining = Math.max(0, cap - otherSum);
        const n = Number(v);
        if (!isNaN(n) && cap > 0 && n > remaining) {
          val = remaining;
          toast.error(`Row capped at remaining excess (${remaining.toFixed(2)} ${lot.unit || ""})`);
        }
      }
    }
    return {
      ...f,
      boxes: (f.boxes || []).map((r, x) => x === i ? { ...r, [k]: val } : r),
    };
  });

  const saveBulk = async (asDraft = false) => {
    clearErrors();
    // STOCK-PACK-01 · Stock SO/ESO + exact line are required for drafts too.
    if (bulkForm.source_type === "stock" && !bulkForm.stock_order_id) return markErr("bulk.stock_order_id", `Pick a ${isExportMode ? "Stock ESO" : "Stock SO"} — required for Pack Against → Stock`);
    if (bulkForm.source_type === "stock" && !bulkForm.stock_line_id) return markErr("bulk.stock_line_id", "Pick the exact stock order line");
    if (asDraft) {
      // Draft-mode bulk: just needs a customer + at least one row so the
      // batch is identifiable in the queue.
      if (!bulkForm.customer_id && bulkForm.source_type !== "stock") {
        return markErr("bulk.customer_id", "Pick a Customer — required even for Draft");
      }
    } else {
    // Feb 2026 · Customer is MANDATORY when packing against a Sales Order.
    // Stock slips can still be created without a customer (linked later via
    // Auto Allocate / Convert to SO), but SO-linked bulk create needs the
    // customer to guarantee the filtered Sales Order picker is meaningful.
    if (bulkForm.source_type === "sales_order" && !bulkForm.customer_id) {
      return toast.error("Pick a Customer first — Customer is mandatory when packing against a Sales Order");
    }
    if (bulkForm.source_type === "export_order" && !bulkForm.customer_id) {
      return toast.error("Pick a Buyer/Customer first — required when packing against an Export Sales Order");
    }
    // Feb 28, 2026 · Excess vs SO / ESO now supports an explicit order picker
    // — mandate it so the auto-queued excess entry ties back to the right SO.
    if (bulkForm.source_type === "excess_so" && !bulkForm.order_id) {
      return toast.error("Pick a Sales Order — required for Excess vs SO");
    }
    if (bulkForm.source_type === "excess_eso" && !bulkForm.export_order_id) {
      return toast.error("Pick an Export Sales Order — required for Excess vs ESO");
    }
    if (!bulkForm.product_code) return markErr("bulk.product_code", "Pick an Article / Product Code");
    if (!bulkForm.pack_date)    return markErr("bulk.pack_date", "Pack Date is required");
    // Mar 2026 · Style No is picked up from the SO/ESO — not every product
    // carries one, so it's NOT compulsory. (Was required before.)
    const n = (bulkForm.boxes || []).length;
    if (n < 1 || n > 200) return toast.error("Add between 1 and 200 box lines");
    // Mar 2026 · Every bulk box must carry all four data-entry numbers
    // (remarks stays optional). Reject the whole batch on the first miss
    // so nothing lands half-populated.
    for (let i = 0; i < n; i++) {
      const b = bulkForm.boxes[i];
      const rowTag = ` (row ${i + 1})`;
      if (!(Number(b.meters) > 0))       return markErr(`bulk.box.${i}.meters`,      `Meters must be greater than 0${rowTag}`);
      if (!(Number(b.joints) > 0))       return markErr(`bulk.box.${i}.joints`,      `Joints must be greater than 0${rowTag}`);
      if (!(Number(b.net_weight) > 0))   return markErr(`bulk.box.${i}.net_weight`,  `Net Weight must be greater than 0${rowTag}`);
      if (!(Number(b.tare_weight) > 0))  return markErr(`bulk.box.${i}.tare_weight`, `Tare Weight must be greater than 0${rowTag}`);
    }
    // Feb 27, 2026 · Excess-mode guards for bulk dispatch.
    if (bulkForm.source_type === "excess_so" || bulkForm.source_type === "excess_eso") {
      // Feb 28, 2026 · Pack-First flow — lot pick is optional; keep cap if picked.
      if (selectedBulkExcessLot) {
        const cap = Number(selectedBulkExcessLot.excess_qty) || 0;
        const sum = (bulkForm.boxes || []).reduce((s, b) => s + (Number(b.meters) || 0), 0);
        if (cap > 0 && sum > cap) {
          return toast.error(`Total meters (${sum.toFixed(2)}) exceed approved excess qty (${cap} ${selectedBulkExcessLot.unit || ""})`);
        }
      }
    }
    } // end !asDraft
    setBulkSaving(true);
    try {
      const payload = {
        source_type: bulkForm.source_type === "stock" ? (isExportMode ? "export_order" : "sales_order") : bulkForm.source_type,
        // STOCK-PACK-01 · every generated slip links the selected Stock SO/ESO + line.
        pack_against: bulkForm.source_type === "stock" ? "stock" : "",
        stock_line_id: bulkForm.source_type === "stock" ? (bulkForm.stock_line_id || "") : "",
        // Feb 2026 · Customer travels in the payload so the backend can
        // enforce the "customer mandatory for SO/ESO" rule and stock slips
        // that DO have a customer keep it for later Auto Allocate matches.
        customer_id: bulkForm.customer_id || "",
        order_id: bulkForm.source_type === "stock" ? (isExportMode ? "" : bulkForm.stock_order_id || "") : (bulkForm.source_type === "sales_order" || bulkForm.source_type === "excess_so") ? (bulkForm.order_id || "") : "",
        export_order_id: bulkForm.source_type === "stock" ? (isExportMode ? bulkForm.stock_order_id || "" : "") : (bulkForm.source_type === "export_order" || bulkForm.source_type === "excess_eso") ? (bulkForm.export_order_id || "") : "",
        excess_entry_id: (bulkForm.source_type === "excess_so" || bulkForm.source_type === "excess_eso") ? (bulkForm.excess_entry_id || "") : "",
        is_excess_dispatch: bulkForm.source_type === "excess_so" || bulkForm.source_type === "excess_eso",
        pack_date: bulkForm.pack_date,
        remarks: bulkForm.remarks || "",
        product_code: bulkForm.product_code,
        item_id: bulkForm.item_id || "",
        style_no: (bulkForm.style_no || "").trim(),
        // Mar 2026 · Draft-mode stamps `status:"Draft"` on the created slips
        // so downstream reports (DC / PL) can filter them out.
        status: asDraft ? "Draft" : undefined,
        boxes: (bulkForm.boxes || []).map(b => ({
          meters: Number(b.meters) || 0,
          joints: parseInt(b.joints) || 0,
          net_weight: Number(b.net_weight) || 0,
          tare_weight: Number(b.tare_weight) || 0,
        })),
      };
      const { data } = await api.post("/packing-slips/bulk", payload);
      const created = data.created || [];
      toast.success(`Created ${data.count} Packing Slips · ${data.slip_nos[0]} → ${data.slip_nos[data.slip_nos.length - 1]}`);
      setBulkOpen(false);
      // Auto-select the newly-created slips AND fire the print flow immediately
      // (Feb 2026) so the operator jumps straight into printing all N labels
      // without hunting through the listing checkbox-by-checkbox.
      if (created.length > 0) {
        setSelected(new Set(created.map(s => s.id)));
        // Delay slightly so the toast is visible before the popup grabs focus.
        setTimeout(() => { try { printLabelsMulti(created); } catch (_) { /* popup blocked — user can still bulk-print manually */ } }, 350);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk create failed");
    } finally {
      setBulkSaving(false);
    }
  };

  const load = async () => {
    // Sep 03, 2026 · Same 403-tolerant pattern as SalesOrders. A packing
    // role may have `sales_orders_slips:view` but not `sales_orders:view`
    // — the missing feeds just render as empty pickers instead of tearing
    // down the whole tab with an uncaught rejection.
    const [s, o, it, cu, ar, eo, pls, epls] = await Promise.all([
      api.get("/packing-slips/").catch(() => ({ data: [] })),
      api.get("/sales-orders/").catch(() => ({ data: [] })),
      api.get("/items/").catch(() => ({ data: [] })),
      api.get("/customers/").catch(() => ({ data: [] })),
      api.get("/articles/").catch(() => ({ data: [] })),
      api.get("/exports/orders/").catch(() => ({ data: [] })),
      api.get("/packing-lists/").catch(() => ({ data: [] })),
      api.get("/exports/packing-lists/").catch(() => ({ data: [] })),
    ]);
    setSlips(s.data); setOrders(dedupById(o.data)); setItems(it.data); setCustomers(cu.data || []);
    // Feb 2026 · Smart Lock filter — hide retired FG SKUs from PS pickers.
    setArticles((ar.data || []).filter(a => a.is_active !== false));
    setExportOrders(eo.data || []);
    // Build ps → pl_no lookup so the row-actions can show a lock badge and
    // disable Edit / Delete / Unlink for packed slips.
    // Mar 2, 2026 · Also fold in Export Packing Lists — an Export-tagged
    // slip pulled into an EPL must show the EPL link chip in the grid,
    // same UX as domestic slips.
    const locked = new Set();
    const map = {};
    for (const pl of (pls.data || [])) {
      for (const sid of (pl.packing_slip_ids || [])) {
        locked.add(sid);
        if (!map[sid]) map[sid] = pl.pl_no || "";
      }
    }
    for (const epl of (epls.data || [])) {
      for (const sid of (epl.linked_internal_ps_ids || [])) {
        locked.add(sid);
        if (!map[sid]) map[sid] = epl.doc_no || "";
      }
    }
    setLockedPsIds(locked);
    setLockedPsMap(map);
  };
  useEffect(() => { load(); }, []);

  // Auto-clear the per-slip preset override whenever the View dialog closes or
  // jumps to a different slip, so a size chosen for slip A never leaks onto B.
  useEffect(() => { setViewPreset(null); }, [preview && preview.id]);

  // Next-step handoff — if the previous tab (Orders) queued a SO for us,
  // auto-open the New Packing Slip dialog with it prefilled.
  useEffect(() => {
    if (!orders.length) return;
    let orderId = "";
    try { orderId = sessionStorage.getItem("mes_ps_next_order") || ""; } catch (e) { /* noop */ }
    if (!orderId) return;
    try { sessionStorage.removeItem("mes_ps_next_order"); } catch (e) { /* noop */ }
    setEditing(null);
    setForm({ order_id: orderId, pack_date: new Date().toISOString().slice(0, 10), remarks: "", boxes: [emptyBox()] });
    setSourceType("sales_order");
    setOpen(true);
  }, [orders]);

  // Bulk-from-SO handoff (Feb 2026). Orders tab's per-line "Bulk Slips"
  // shortcut writes { order_id, customer_id, product_code, item_id, style_no }
  // into sessionStorage `mes_ps_next_bulk` then navigates here. Consume the
  // payload once, then wipe it so refreshes don't re-open the dialog.
  useEffect(() => {
    if (!orders.length) return;
    let raw = "";
    try { raw = sessionStorage.getItem("mes_ps_next_bulk") || ""; } catch (e) { /* noop */ }
    if (!raw) return;
    try { sessionStorage.removeItem("mes_ps_next_bulk"); } catch (e) { /* noop */ }
    let payload = null;
    try { payload = JSON.parse(raw); } catch (e) { return; }
    if (!payload) return;
    setBulkForm({
      ...emptyBulk(),
      source_type: "sales_order",
      customer_id: payload.customer_id || "",
      order_id: payload.order_id || "",
      product_code: payload.product_code || "",
      item_id: payload.item_id || "",
      style_no: payload.style_no || "",
    });
    setBulkOpen(true);
  }, [orders, articles]);

  const openNew = () => { setEditing(null); setForm({ order_id: "", export_order_id: "", excess_entry_id: "", stock_order_id: "", stock_line_id: "", pack_date: new Date().toISOString().slice(0, 10), remarks: "", boxes: [emptyBox()] }); setSourceType(isExportMode ? "export_order" : "sales_order"); setStyleFilter(""); setOpen(true); };
  const openEdit = (r) => {
    const inferredSource = r.source_type || (r.export_order_id ? "export_order" : (r.order_id ? "sales_order" : "stock"));
    setEditing(r);
    setForm({ order_id: r.order_id || "", export_order_id: r.export_order_id || "", pack_date: r.pack_date, remarks: r.remarks || "", boxes: [{ ...emptyBox(), ...((r.boxes && r.boxes[0]) || {}) }],
      stock_order_id: r.pack_against === "stock" ? (r.export_order_id || r.order_id || "") : "", stock_line_id: r.stock_line_id || "" });
    setSourceType(r.pack_against === "stock" ? "stock" : inferredSource);
    setStyleFilter("");
    setOpen(true);
  };
  // Duplicate as Next Box — start a NEW slip (backend will auto-generate a
  // fresh PS-YY-NNN), pre-filling Product Code + Tare Weight from the source.
  // Meters / Joints / Net are cleared so the operator only fills variables.
  const openDuplicate = (r) => {
    const src = (r.boxes && r.boxes[0]) || {};
    setEditing(null);
    setForm({
      order_id: r.order_id || "",
      export_order_id: r.export_order_id || "",
      stock_order_id: r.pack_against === "stock" ? (r.export_order_id || r.order_id || "") : "",
      stock_line_id: r.stock_line_id || "",
      pack_date: new Date().toISOString().slice(0, 10),
      remarks: r.remarks || "",
      boxes: [{
        ...emptyBox(),
        item_id: src.item_id || "",
        product_code: src.product_code || "",
        tare_weight: Number(src.tare_weight) || 0,
      }],
    });
    setSourceType(r.pack_against === "stock" ? "stock" : (r.source_type || (r.export_order_id ? "export_order" : (r.order_id ? "sales_order" : "stock"))));
    setOpen(true);
    toast.info(`Cloned ${r.slip_no} — fill Meters / Joints / Net for the next box`);
  };

  const [styleFilter, setStyleFilter] = useState("");

  // Items allowed for the box's Product Code dropdown:
  //  - if slip is against STOCK → full Item Master (loose inventory / samples)
  //  - if slip is against a SO with a picked order → restrict to its own line
  //    items (article_no values from the SO are the source of truth)
  //  - SO mode with no order picked yet → empty (forces the operator to pick)
  const allowedItems = useMemo(() => {
    if (sourceType === "stock") {  // STOCK-PACK-01 · only the selected Stock SO/ESO line
      const so = (isExportMode ? exportOrders : orders).find(o => o.id === form.stock_order_id);
      const l = stockLinesOf(so, isExportMode).find(x => x.line_id === form.stock_line_id);
      return l ? [{ id: l.item_id || l.code, code: l.code, style_no: l.style_no }] : [];
    }
    if (sourceType === "export_order") {
      if (!form.export_order_id) return [];
      const eso = exportOrders.find(o => o.id === form.export_order_id);
      if (!eso) return [];
      const seen = new Set();
      return (eso.items || []).filter(li => li.article_no).map(li => ({
        id: li.article_no,
        code: li.article_no,
        style_no: li.style_no || "",
      })).filter(o => { if (seen.has(o.id)) return false; seen.add(o.id); return true; });
    }
    if (!form.order_id) return [];
    const order = orders.find(o => o.id === form.order_id);
    if (!order) return [];
    // Build synthetic option list from the SO line items so the dropdown
    // works even if the Item Master doesn't yet have those articles.
    const seen = new Set();
    return (order.items || []).filter(li => li.article_no).map(li => {
      const id = li.article_id || li.item_id || li.article_no;
      return { id, code: li.article_no, style_no: li.style_no || "" };
    }).filter(o => {
      if (seen.has(o.id)) return false;
      seen.add(o.id);
      return true;
    });
  }, [sourceType, form.order_id, form.export_order_id, form.stock_order_id, form.stock_line_id, isExportMode, orders, exportOrders, items]);

  // Feb 27, 2026 · Selected approved-excess lot for the picker → caps the
  // meters input at the lot's `excess_qty` in both single and bulk flows,
  // and travels in the save payload so the backend can settle the entry.
  const selectedExcessLot = useMemo(
    () => approvedLots.find(l => l.id === form.excess_entry_id) || null,
    [approvedLots, form.excess_entry_id],
  );
  const selectedBulkExcessLot = useMemo(
    () => approvedLots.find(l => l.id === bulkForm.excess_entry_id) || null,
    [approvedLots, bulkForm.excess_entry_id],
  );

  // Style-No search box (Feb 2026) — narrows the Product Code dropdown to just
  // the line items whose `style_no` contains the typed substring. Empty query
  // = show everything. Case-insensitive.
  const filteredAllowedItems = useMemo(() => {
    const q = styleFilter.trim().toLowerCase();
    if (!q) return allowedItems;
    return allowedItems.filter(it => (it.style_no || "").toLowerCase().includes(q));
  }, [allowedItems, styleFilter]);

  // When the Sales Order changes, clear the previously-selected Product Code so
  // the operator doesn't accidentally save a mismatched product.
  const onOrderChange = (v) => setForm(f => ({
    ...f,
    order_id: v,
    boxes: f.boxes.map(b => ({ ...b, item_id: "", product_code: "" })),
  }));
  // Same behaviour when the Export SO changes.
  const onExportOrderChange = (v) => setForm(f => ({
    ...f,
    export_order_id: v,
    boxes: f.boxes.map(b => ({ ...b, item_id: "", product_code: "" })),
  }));

  // ONE box per Packing Slip — helpers kept as no-ops for the sole surviving box row.
  const updateBox = (i, key, v) => setForm(f => ({
    ...f,
    boxes: f.boxes.map((b, x) => {
      if (x !== i) return b;
      let val = v;
      // Feb 27, 2026 · Excess-mode meters cap. When an approved lot is
      // picked, the box's meters can't exceed the lot's `excess_qty`.
      if (key === "meters" && (sourceType === "excess_so" || sourceType === "excess_eso") && selectedExcessLot) {
        const cap = Number(selectedExcessLot.excess_qty) || 0;
        const n = Number(v);
        if (!isNaN(n) && cap > 0 && n > cap) {
          val = cap;
          toast.error(`Meters capped at approved excess qty (${cap} ${selectedExcessLot.unit || ""})`);
        }
      }
      const next = { ...b, [key]: val };
      if (key === "net_weight" || key === "tare_weight") {
        next.gross_weight = (Number(next.net_weight) || 0) + (Number(next.tare_weight) || 0);
      }
      return next;
    }),
  }));

  const save = async (asDraft = false) => {
    clearErrors();
    // Mar 2026 · Save as Draft skips the "compulsory field" gates so
    // operators can park a partial slip mid-shift and finish it later.
    // Only remarks + product_code presence on at least one box are
    // required at Draft time so the slip is still identifiable.
    if (asDraft) {
      if (!form.boxes.some(b => b.product_code)) {
        return markErr("form.boxes.0.product_code", "Add at least one box with Product Code — required even for Draft");
      }
    } else {
      // All data-entry fields are compulsory except `Remarks`. Guards run
      // in field order so operators see the first missing input, not a
      // generic "invalid" toast.
      if (!form.pack_date) return markErr("form.pack_date", "Pack Date is required");
      if ((sourceType === "sales_order" || sourceType === "excess_so") && !form.order_id) {
        return markErr("form.order_id", "Pick a Sales Order — required for this slip type");
      }
      if ((sourceType === "export_order" || sourceType === "excess_eso") && !form.export_order_id) {
        return markErr("form.export_order_id", "Pick an Export Sales Order — required for this slip type");
      }
      if (sourceType === "stock" && !form.stock_order_id) return markErr("form.stock_order_id", `Pick a ${isExportMode ? "Stock ESO" : "Stock SO"} — required for Pack Against → Stock`);
      if (sourceType === "stock" && !form.stock_line_id) return markErr("form.stock_line_id", "Pick the exact stock order line");
      if ((sourceType === "excess_so" || sourceType === "excess_eso") && !form.excess_entry_id) {
        return markErr("form.excess_entry_id", "Pick the approved Excess Production lot — required for excess dispatch");
      }
      if (!(form.boxes || []).length) return toast.error("Add at least one box");
      for (let i = 0; i < form.boxes.length; i++) {
        const b = form.boxes[i];
        const rowTag = form.boxes.length > 1 ? ` (row ${i + 1})` : "";
        if (!b.product_code)               return markErr(`form.boxes.${i}.product_code`, `Product Code is required${rowTag}`);
        if (!(Number(b.meters) > 0))       return markErr(`form.boxes.${i}.meters`,       `Meters must be greater than 0${rowTag}`);
        if (!(Number(b.joints) > 0))       return markErr(`form.boxes.${i}.joints`,       `Joints must be greater than 0${rowTag}`);
        if (!(Number(b.net_weight) > 0))   return markErr(`form.boxes.${i}.net_weight`,   `Net Weight must be greater than 0${rowTag}`);
        if (!(Number(b.tare_weight) > 0))  return markErr(`form.boxes.${i}.tare_weight`,  `Tare Weight must be greater than 0${rowTag}`);
        if (!(Number(b.gross_weight) > 0)) return markErr(`form.boxes.${i}.gross_weight`, `Gross Weight must be greater than 0${rowTag}`);
      }
    }
    // Feb 27, 2026 · Excess dispatch guards — lot MUST be picked, and total
    // meters across boxes cannot exceed the lot's approved excess_qty.
    if (sourceType === "excess_so" || sourceType === "excess_eso") {
      // Feb 28, 2026 · Pack-First flow — lot picking is now OPTIONAL. If
      // no lot is picked we still cap by lot when one IS selected; otherwise
      // the slip auto-posts to the Excess Production Queue via the backend.
      if (selectedExcessLot) {
        const cap = Number(selectedExcessLot.excess_qty) || 0;
        const sum = (form.boxes || []).reduce((s, b) => s + (Number(b.meters) || 0), 0);
        if (cap > 0 && sum > cap) {
          return toast.error(`Total meters (${sum.toFixed(2)}) exceed approved excess qty (${cap} ${selectedExcessLot.unit || ""})`);
        }
      }
    }
    try {
      const payload = {
        ...form,
        // Mar 2026 · Explicit Draft status when saving unfinished slips.
        // Backend accepts free-form `status`; Confirmed slips leave the
        // default in place so downstream approval/dispatch keeps working.
        status: asDraft ? "Draft" : (form.status || undefined),
        // Persist source intent + clear scope IDs that don't apply to the
        // current mode so a slip never carries stale linkage after a toggle.
        source_type: sourceType === "stock" ? (isExportMode ? "export_order" : "sales_order") : sourceType,
        // STOCK-PACK-01 · Pack Against → Stock links the exact Stock SO/ESO + line.
        pack_against: sourceType === "stock" ? "stock" : "",
        stock_line_id: sourceType === "stock" ? (form.stock_line_id || "") : "",
        // Feb 27, 2026 · Excess dispatch tabs share the same
        // `order_id / export_order_id` fields as their base SO/ESO
        // counterparts + an explicit `is_excess_dispatch` tag so the
        // dispatch ledger can filter/report on them separately.
        order_id: sourceType === "stock" ? (isExportMode ? "" : form.stock_order_id || "") : (sourceType === "sales_order" || sourceType === "excess_so") ? (form.order_id || "") : "",
        export_order_id: sourceType === "stock" ? (isExportMode ? form.stock_order_id || "" : "") : (sourceType === "export_order" || sourceType === "excess_eso") ? (form.export_order_id || "") : "",
        excess_entry_id: (sourceType === "excess_so" || sourceType === "excess_eso") ? (form.excess_entry_id || "") : "",
        is_excess_dispatch: sourceType === "excess_so" || sourceType === "excess_eso",
        boxes: form.boxes.map(b => ({
          ...b,
          meters: Number(b.meters) || 0,
          joints: parseInt(b.joints) || 0,
          net_weight: Number(b.net_weight) || 0,
          tare_weight: Number(b.tare_weight) || 0,
          gross_weight: Number(b.gross_weight) || 0,
        })),
      };
      let savedOrderId = form.order_id;
      let savedSlipNo = editing?.slip_no || "";
      if (editing) {
        const { data } = await api.put(`/packing-slips/${editing.id}`, payload);
        savedOrderId = data?.order_id || savedOrderId;
        savedSlipNo = data?.slip_no || savedSlipNo;
      } else {
        const { data } = await api.post("/packing-slips/", payload);
        savedOrderId = data?.order_id || savedOrderId;
        savedSlipNo = data?.slip_no || "";
      }
      // Workflow nudge — offer to jump to Packing Lists with the same SO prefilled.
      if (!editing && savedOrderId && onNextStep) {
        toast.success(`Packing Slip ${savedSlipNo} created`, {
          action: {
            label: "→ Create Packing List",
            onClick: () => onNextStep("packing", savedOrderId),
          },
          duration: 8000,
        });
      } else {
        toast.success(editing ? "Packing Slip updated" : `Packing Slip ${savedSlipNo} created`);
      }
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const remove = async (id) => {
    if (!rbacReady) return;
    if (!canDeleteSlip) { toast.error(`🔒 ${slipModule}.delete — ask an admin to grant this`); return; }
    if (!confirm("Delete this Packing Slip?")) return;
    await api.delete(`/packing-slips/${id}`);
    toast.success("Deleted"); load();
  };

  // Convert-to-SO flow (Feb 2026): stock-mode slips can be re-linked to a
  // Sales Order after packing. We validate that the box's Product Code is
  // one of the picked SO's line-item article_nos so we never attach a slip
  // to an SO that doesn't actually order that product.
  const [convertTarget, setConvertTarget] = useState(null);
  const [convertSoId, setConvertSoId] = useState("");
  const openConvert = (slip) => { setConvertTarget(slip); setConvertSoId(""); };
  const closeConvert = () => { setConvertTarget(null); setConvertSoId(""); };
  const convertToSO = async () => {
    if (!convertTarget || !convertSoId) return;
    const order = orders.find(o => o.id === convertSoId);
    if (!order) return toast.error("Pick a Sales Order");
    const slipPc = ((convertTarget.boxes && convertTarget.boxes[0]) || {}).product_code || "";
    const soPcs = new Set((order.items || []).map(li => li.article_no).filter(Boolean));
    if (slipPc && soPcs.size > 0 && !soPcs.has(slipPc)) {
      return toast.error(`Product Code "${slipPc}" is not on ${order.order_no} line items`);
    }
    try {
      // Feb 2026 · dedicated endpoint that also back-fills each box's `line_id`
      // via `_resolve_line_id_for_box` — closes the stock-slip traceability
      // gap so 100 % of dispatch history becomes line-addressable.
      const { data } = await api.post(`/packing-slips/${convertTarget.id}/link-to-so`, { order_id: convertSoId });
      toast.success(`${convertTarget.slip_no} → linked to ${order.order_no} · ${data.boxes_matched}/${data.boxes_total} boxes matched`);
      closeConvert(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Convert failed"); }
  };

  // Unlink-from-SO flow (Feb 2026): reverse of convertToSO. Detaches an
  // SO-linked slip so it becomes a stock slip. Guardrail: block unlinking
  // if the slip is already consumed by a Packing List (PL) — the PL would
  // suddenly reference a stock slip which breaks the traceability chain.
  // Feb 2026 · Auto Allocate — MANUAL trigger only. Splits this slip into N
  // child slips linked to matching approved Sales Orders (FIFO by sales_date).
  // Slip stays as the audit parent; children are the SO-linked delivery-ready
  // slips. If slip.customer_id is missing, we prompt for one inline via a
  // second POST. Never runs during create/edit — button click only.
  const autoAllocate = async (slip) => {
    let customerId = slip.customer_id || "";
    if (!customerId) {
      customerId = window.prompt(`Customer Code is blank on ${slip.slip_no}. Enter Customer ID to allocate against:`) || "";
      if (!customerId) return;
    }
    if (!confirm(`Auto-allocate ${slip.slip_no}?\nMatching approved Sales Orders will be filled FIFO. Child slips will be created — the parent slip stays as audit.`)) return;
    try {
      const { data } = await api.post(`/packing-slips/${slip.id}/auto-allocate`, { customer_id: customerId });
      if (data.status === "no_match") {
        toast.error(`No pending Sales Order found for customer / product ${data.product_code} · ${data.packing_qty} m`);
        return;
      }
      toast.success(
        `${data.slip_no} · ${data.allocation_status} · allocated ${data.allocated_qty} m across ${data.sales_orders_updated} SO${data.sales_orders_updated === 1 ? "" : "s"}${data.remaining_qty > 0 ? ` · ${data.remaining_qty} m remaining` : ""}`,
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto Allocate failed");
    }
  };

  const unlinkToStock = async (slip) => {
    const on = orderNo(slip.order_id);
    if (!confirm(`Unlink ${slip.slip_no} from ${on}?\nIt will become a Stock slip (no SO linkage).`)) return;
    try {
      await api.put(`/packing-slips/${slip.id}`, {
        ...slip,
        order_id: "",
        source_type: "stock",
      });
      toast.success(`${slip.slip_no} unlinked from ${on} → moved to Stock`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Unlink failed"); }
  };

  const orderNo = (id) => id ? (orders.find(o => o.id === id)?.order_no || "—") : "—";
  const exportOrderNo = (id) => id ? (exportOrders.find(o => o.id === id)?.doc_no || "—") : "—";
  // SOESO-FULFIL-01 · STOCK label derived from the source order's stock classification.
  const slipIsStock = (s) => isStockOrder(s?.export_order_id ? exportOrders.find(o => o.id === s.export_order_id) : orders.find(o => o.id === s?.order_id));
  const slipRefText = (s) => `${s.export_order_id ? exportOrderNo(s.export_order_id) : orderNo(s.order_id)}${slipIsStock(s) ? " · STOCK" : ""}${s.stock_line_no ? ` · Line ${s.stock_line_no}` : ""}`;
  const orderPo = (id) => id ? (orders.find(o => o.id === id)?.po_number || "") : "";
  // Feb 2026 — unified Buyer-PO lookup: SO.po_number for domestic, ESO.buyer_po_no
  // for exports. Used by the barcode label so warehouses see the customer's own
  // PO reference regardless of whether the slip is domestic or export.
  const buyerPoFor = (order_id, export_order_id = "") => {
    if (order_id) return orderPo(order_id);
    if (export_order_id) {
      return exportOrders.find(o => o.id === export_order_id)?.buyer_po_no || "";
    }
    return "";
  };
  // Customer name resolver (Feb 2026) — SO → customer_id → customers roster.
  // Returns "" when no SO is bound so stock slips fall through cleanly.
  const customerNameFor = (orderId, exportOrderId = "") => {
    if (orderId) {
      const so = orders.find(o => o.id === orderId);
      if (so) return customers.find(c => c.id === so.customer_id)?.name || "";
    }
    if (exportOrderId) {
      const eso = exportOrders.find(o => o.id === exportOrderId);
      if (eso) {
        // Prefer Customer Master name via buyer_customer_id, fall back to buyer_name.
        return (customers.find(c => c.id === eso.buyer_customer_id)?.name) || eso.buyer_name || "";
      }
    }
    return "";
  };
  // Look up the Customer's Style Number for a given (order_id, product_code)
  // by scanning the SO's line items for a match on article_no / item_code.
  // Falls back to the stored `box.style_no` (bulk / stock slips) when the SO
  // lookup returns empty so bulk-generated stock slips still render Style
  // on labels and in the listing. Feb 2026.
  const styleNoFor = (order_id, product_code, box_style_no = "", export_order_id = "") => {
    // 1. SO branch
    if (order_id && product_code) {
      const so = orders.find(o => o.id === order_id);
      if (so && Array.isArray(so.items)) {
        const pc = String(product_code).trim().toLowerCase();
        const line = so.items.find(li => {
          const cand = [li.article_no, li.item_code, li.item_no].filter(Boolean).map(s => String(s).trim().toLowerCase());
          return cand.includes(pc);
        });
        if (line && line.style_no) return String(line.style_no);
      }
    }
    // 2. ESO branch (Feb 2026)
    if (export_order_id && product_code) {
      const eso = exportOrders.find(o => o.id === export_order_id);
      if (eso && Array.isArray(eso.items)) {
        const pc = String(product_code).trim().toLowerCase();
        const line = eso.items.find(li => String(li.article_no || "").trim().toLowerCase() === pc);
        if (line && line.style_no) return String(line.style_no);
      }
    }
    // 3. Article Master fallback (Feb 2026) — every Article carries its own
    // canonical `style_no`, so labels stay complete even when the SO/ESO
    // line didn't get one filled in.
    if (product_code) {
      const pc = String(product_code).trim().toLowerCase();
      const a = articles.find(x => String(x.product_code || x.article_no || x.code || "").trim().toLowerCase() === pc);
      if (a && a.style_no) return String(a.style_no);
    }
    return box_style_no ? String(box_style_no) : "";
  };

  const filteredSlips = useMemo(() => {
    const q = search.trim().toLowerCase();
    const stamp = (s) => s.created_at || s.updated_at || s.pack_date || "";
    // Mar 2026 · Digit-aware slip_no tiebreaker — extract the trailing
    // numeric segment and compare as an int so `PS-1000` correctly
    // sorts AFTER `PS-999` (localeCompare numeric handles it too but
    // this is explicit + resilient to prefix drift).
    const slipNoInt = (s) => {
      const m = String(s || "").match(/(\d+)(?!.*\d)/);
      return m ? parseInt(m[1], 10) : 0;
    };
    const sortDesc = (a, b) => {
      const cmp = String(stamp(b)).localeCompare(String(stamp(a)));
      if (cmp !== 0) return cmp;
      const n = slipNoInt(b.slip_no) - slipNoInt(a.slip_no);
      if (n !== 0) return n;
      return (b.slip_no || "").localeCompare(a.slip_no || "", undefined, { numeric: true, sensitivity: "base" });
    };
    const modePass = (s) => {
      const isExportRow = (s.slip_no || "").startsWith("EPS-")
        || ["export_order", "excess_eso"].includes(s.source_type || "")
        || !!s.export_order_id;
      if (isExportMode) return isExportRow;
      if (isDomesticMode) return !isExportRow;
      return true;
    };
    const base = slips.filter(modePass);
    if (!q) return [...base].sort(sortDesc);
    return base.filter(s => {
      const on = orderNo(s.order_id).toLowerCase();
      const pc = ((s.boxes && s.boxes[0]?.product_code) || "").toLowerCase();
      return (s.slip_no || "").toLowerCase().includes(q) || on.includes(q) || pc.includes(q);
    }).sort(sortDesc);
  }, [slips, search, orders, isExportMode, isDomesticMode]);
  // Mar 2026 · Reset pagination when search / mode filter changes so the
  // user sees the newest match on top instead of a stale scroll offset.
  useEffect(() => { setVisibleCount(PS_PAGE_SIZE); }, [search, isExportMode, isDomesticMode]);

  const printLabels = (slip, presetOverride) => printLabelsMulti([slip], presetOverride);
  const printLabelsMulti = (slipList, presetOverride) => {
    // Thermal barcode labels — TSC TE244 (203 DPI). Layout adapts per preset:
    //   • paired=true  → one page per box, FRONT top + BACK bottom (dashed cut-line).
    //   • paired=false → one page = one sticker (dispatch tag / spool label).
    // Feb 2026 · uses a hidden <iframe> instead of `window.open()` so shared-
    // printer queues stay clean, popup-blockers can't kill the flow, and
    // every batch prints as ONE merged job (browser's Save-as-PDF also emits
    // a single merged PDF, satisfying the "merged PDF" user brief).
    const activeKey = (presetOverride && LABEL_PRESETS[presetOverride]) ? presetOverride : labelPreset;
    const P = LABEL_PRESETS[activeKey] || LABEL_PRESETS["4x6"];
    const stickerHTML = (slip, b, side) => {
      const po = buyerPoFor(slip.order_id, slip.export_order_id);
      const styleNo = styleNoFor(slip.order_id, b.product_code, b.style_no, slip.export_order_id);
      const custName = customerNameFor(slip.order_id, slip.export_order_id);
      // Feb 2026 label layout — refined line hierarchy per operator brief:
      //   1. Box No (largest — kept unchanged)
      //   2. PS-number + date — 50 % font (smaller than default hdr)
      //   3. Customer name — bold, own line
      //   4. Style + PO — bold, combined on one line
      //   5. SO number — normal weight, own line
      //   6. Product / Meters / Weights — unchanged
      // Bottom-right corner: mini QR encoding `slip_no|box_no|line_id`
      // so a picker's handheld scanner can jump straight to the SO line.
      const psLine = P.showAll
        ? `<div class="ps-line">${slip.slip_no}  ·  ${slip.pack_date}${side ? `  ·  <span class="side">${side}</span>` : ""}</div>`
        : (P.paired || labelPresetKey === "4x2"
          ? `<div class="hdr">${slip.slip_no}  ·  ${slip.pack_date}${side ? `  ·  <span class="side">${side}</span>` : ""}</div>`
          : "");
      const customerLine = P.showAll && custName
        ? `<div class="row cust-bold"><b>Customer:</b> ${custName}</div>`
        : "";
      const styleAndPo = [
        styleNo ? `<b>Style:</b> ${styleNo}` : "",
        po ? `<b>PO:</b> ${po}` : "",
      ].filter(Boolean).join("   ·   ");
      const styleLine = P.showAll && styleAndPo
        ? `<div class="row style-po-bold">${styleAndPo}</div>`
        : "";
      const soLine = P.showAll && (slip.order_id || slip.export_order_id)
        ? `<div class="row"><b>${slip.export_order_id ? "ESO" : "SO"}:</b> ${slipRefText(slip)}</div>${slip.created_from_fg ? `<div class="row"><b>From FG Stock:</b> ${(slip.fg_source_stock_orders || []).map(n => `${n} · STOCK`).join(", ")}</div>` : ""}`
        : "";
      const detailRows = P.showAll ? `
          <div class="row"><b>Meters:</b> ${b.meters} m  ·  <b>Joints:</b> ${b.joints}</div>
          <div class="row"><b>Net:</b> ${fmtQty(b.net_weight || 0, "KG")}  ·  <b>Tare:</b> ${fmtQty(b.tare_weight || 0, "KG")}  ·  <b>Gross:</b> ${fmtQty(b.gross_weight || 0, "KG")} kg</div>` : "";
      const productLine = P.showAll || labelPresetKey === "4x2"
        ? `<div class="row"><b>Product:</b> ${b.product_code}</div>`
        : "";
      // QR payload — pipe-delimited so a downstream mobile app can .split('|')
      // to recover the coordinates without JSON parsing overhead.
      const qrPayload = `${slip.slip_no}|${b.box_no}|${b.line_id || ""}`;
      const qrBlock = P.showAll
        ? `<div class="qr-wrap"><canvas class="qr" data-code="${qrPayload}"></canvas></div>`
        : "";
      return `
        <div class="sticker">
          <div class="box">${b.box_no}</div>
          ${psLine}
          ${customerLine}
          ${styleLine}
          ${soLine}
          ${productLine}
          ${detailRows}
          <svg class="bc" data-code="${b.box_no}"></svg>
          ${qrBlock}
        </div>`;
    };
    const pages = slipList.flatMap(slip =>
      (slip.boxes || []).map(b => P.paired ? `
        <div class="page">
          ${stickerHTML(slip, b, "FRONT")}
          <div class="sep"></div>
          ${stickerHTML(slip, b, "BACK")}
        </div>` : `
        <div class="page">${stickerHTML(slip, b, "")}</div>`)
    ).join("");
    // Local alias so the inline `${labelPresetKey === "2x1" …}` template checks
    // below honour the active (possibly-overridden) preset.
    const labelPresetKey = activeKey;
    // Move alias to the top of the function so both the stickerHTML builder
    // and the inline print-page <style> template read the SAME key.
    const titleTag = `TSC TE244 · ${P.label} · ${slipList.length} slip${slipList.length === 1 ? "" : "s"}`;
    // Assemble the full HTML document once, then hand it to a hidden iframe.
    const docHTML = `<!doctype html><html><head><title>${titleTag}</title>
      <style>
        @page { size: ${P.pageW} ${P.pageH}; margin: 0; }
        html, body { margin: 0; padding: 0; background: #fff; color: #000;
                     font-family: Arial, Helvetica, sans-serif;
                     -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .page {
          width: ${P.pageW}; height: ${P.pageH};
          box-sizing: border-box; page-break-after: always;
          display: flex; flex-direction: column;
        }
        .page:last-child { page-break-after: auto; }
        .sticker {
          width: ${P.pageW}; height: ${P.stickerH};
          box-sizing: border-box; padding: ${P.padding}; overflow: hidden;
          position: relative;
        }
        .sep { border-top: 1px dashed #999; }
        .hdr { font-size: calc(${P.hdrFont} - 2pt); letter-spacing: 0.3px; text-transform: uppercase; opacity: 0.85; }
        .ps-line { font-size: calc(${P.rowFont} * 0.5); letter-spacing: 0.3px;
                   text-transform: uppercase; opacity: 0.75; text-align: center;
                   margin: 0.4mm 0 0.8mm 0; }
        .side { font-weight: 900; letter-spacing: 2px; }
        .box { font-size: ${P.boxFont}; font-weight: 900; letter-spacing: 1px;
               margin: ${labelPresetKey === "2x1" ? "0.5mm" : "1.5mm"} 0 0.2mm 0;
               text-align: center; line-height: 1; }
        .row { font-size: ${P.rowFont}; margin: 0.6mm 0; }
        .cust-bold { font-weight: 800; letter-spacing: 0.2px; }
        .style-po-bold { font-weight: 800; letter-spacing: 0.3px; }
        b { font-weight: 700; }
        .bc { display: block; margin: ${labelPresetKey === "2x1" ? "0.5mm" : "2mm"} auto 0 auto;
              width: ${P.bcW}; height: ${P.bcH}; }
        .qr-wrap { position: absolute; bottom: ${P.padding}; right: ${P.padding};
                   background: #fff; }
        .qr { width: 14mm; height: 14mm; display: block; }
      </style>
      <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
      <script src="https://cdn.jsdelivr.net/npm/qrcode@1.5.4/build/qrcode.min.js"><\/script>
      </head><body>${pages}
      <script>window.addEventListener('load', () => {
        document.querySelectorAll('svg.bc').forEach(s => {
          try {
            JsBarcode(s, s.getAttribute('data-code'), {
              format: 'CODE128',
              displayValue: ${labelPresetKey !== "2x1"},
              fontSize: ${P.bcTextSize},
              font: 'Arial',
              textMargin: 1,
              width: ${labelPresetKey === "2x1" ? 1.5 : 2},
              height: ${P.bcSvgHeight},
              margin: 0,
              background: '#FFFFFF',
              lineColor: '#000000',
            });
          } catch(e){}
        });
        document.querySelectorAll('canvas.qr').forEach(c => {
          try {
            if (window.QRCode && QRCode.toCanvas) {
              QRCode.toCanvas(c, c.getAttribute('data-code'), { margin: 0, width: 60 });
            }
          } catch(e){}
        });
        // Give barcode/QR libs a beat to paint, then fire the parent's print.
        setTimeout(() => { try { window.focus(); window.print(); } catch(e){} }, 600);
      });<\/script>
      </body></html>`;
    // Hidden iframe · one merged print job, no popup window. Cleans itself
    // up after the print dialog closes (or after a 60s safety timeout).
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.setAttribute("data-testid", "ps-print-iframe");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
    document.body.appendChild(iframe);
    const cleanup = () => { try { document.body.removeChild(iframe); } catch (_) { /* noop */ } };
    iframe.onload = () => {
      const win = iframe.contentWindow;
      if (!win) { cleanup(); return; }
      if (win.matchMedia) {
        try {
          const mql = win.matchMedia("print");
          if (mql && mql.addEventListener) mql.addEventListener("change", (e) => { if (!e.matches) setTimeout(cleanup, 250); });
        } catch (_) { /* noop */ }
      }
      setTimeout(cleanup, 60000);
    };
    const idoc = iframe.contentDocument || iframe.contentWindow.document;
    idoc.open();
    idoc.write(docHTML);
    idoc.close();
  };

  // ── Bulk selection helpers ────────────────────────────────────────────────
  const toggleSel = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAllFiltered = () => setSelected(prev => {
    const ids = filteredSlips.map(s => s.id);
    const allOn = ids.length > 0 && ids.every(id => prev.has(id));
    const next = new Set(prev);
    if (allOn) ids.forEach(id => next.delete(id));
    else ids.forEach(id => next.add(id));
    return next;
  });
  const bulkPrint = () => {
    const chosen = slips.filter(s => selected.has(s.id));
    if (!chosen.length) return toast.error("Pick at least one slip to bulk-print");
    printLabelsMulti(chosen);
  };
  const clearSelection = () => setSelected(new Set());
  const allFilteredSelected = filteredSlips.length > 0 && filteredSlips.every(s => selected.has(s.id));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-zinc-500">Box-level packing document — each box gets an auto-numbered barcode label. <span className="text-zinc-400">Print format: <b data-testid="ps-current-preset">{LABEL_PRESETS[labelPreset]?.label}</b> · <b>TSC TE244</b> (203 DPI thermal).</span></div>
        <div className="flex items-center gap-2">
          {/* Search: matches Slip No / Order No / Product Code substring.
              Useful when warehouse consolidates a shipment or chases a quality issue. */}
          <div className="relative">
            <MagnifyingGlassIcon size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="ps-search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search Slip / Order / Product Code…"
              className="h-9 pl-8 pr-8 w-80 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-[var(--accent)]"
            />
            {search && (
              <button data-testid="ps-search-clear" onClick={() => setSearch("")} className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded-sm text-zinc-500 hover:text-white hover:bg-[var(--bg-2)]" title="Clear">
                <XIcon size={12} weight="bold" />
              </button>
            )}
          </div>
          {/* Label size preset — swaps @page geometry at runtime (same TE244 driver) */}
          <Select value={labelPreset} onValueChange={setLabelPreset}>
            <SelectTrigger data-testid="ps-label-preset" className="h-9 w-44 bg-[var(--bg-0)] border-[var(--border)] text-sm text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
              {Object.entries(LABEL_PRESETS).map(([k, v]) => (
                <SelectItem key={k} value={k} data-testid={`ps-preset-${k}`}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* 1:1 preview modal — renders a real sticker at physical size so
              the operator can eyeball whether meters/joints/weights fit
              before wasting a print run on a new media size. */}
          <button
            data-testid="ps-preview-label"
            onClick={() => setPreviewLabelOpen(true)}
            title="Preview the selected label size at 1:1 scale"
            className="h-9 px-3 border border-[var(--border)] hover:bg-[var(--bg-2)] text-xs rounded-sm flex items-center gap-2 text-zinc-300"
          >
            <EyeIcon size={14} weight="bold" />Preview
          </button>
          <button data-testid="bulk-ps-btn" onClick={openBulk} disabled={!rbacReady || !canCreateSlip} title={rbacReady && !canCreateSlip ? `🔒 ${slipModule}.create — ask an admin to grant this` : "Create N identically-packed slips at once (Customer → Article → Boxes)"} className={`h-9 px-4 border border-emerald-500/40 hover:bg-emerald-500/10 text-emerald-400 text-sm font-semibold rounded-sm flex items-center gap-2 ${(!rbacReady || !canCreateSlip) ? "opacity-50 cursor-not-allowed" : ""}`}><StackIcon size={14} weight="bold" />Bulk Slips</button>
          <button data-testid="bulk-auto-allocate-btn" onClick={bulkAutoAllocate} disabled={!rbacReady || !canCreateSlip} title={rbacReady && !canCreateSlip ? `🔒 ${slipModule}.create — ask an admin to grant this` : "Auto-allocate every unallocated stock slip in one pass"} className={`h-9 px-4 border border-blue-500/40 hover:bg-blue-500/10 text-blue-300 text-sm font-semibold rounded-sm flex items-center gap-2 ${(!rbacReady || !canCreateSlip) ? "opacity-50 cursor-not-allowed" : ""}`}><MagicWandIcon size={14} weight="bold" />Bulk Auto Allocate</button>
          <button data-testid="alloc-history-btn" onClick={openHistory} title="Allocation history · every parent → child → SO link" className="h-9 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-zinc-300 text-sm font-semibold rounded-sm items-center gap-2 flex"><EyeIcon size={14} weight="bold" />History</button>
        <button data-testid="new-ps-btn" onClick={openNew} disabled={!rbacReady || !canCreateSlip} title={rbacReady && !canCreateSlip ? `🔒 ${slipModule}.create — ask an admin to grant this` : undefined} className={`h-9 px-4 bg-[var(--accent)] text-white text-sm font-semibold rounded-sm flex items-center gap-2 ${(!rbacReady || !canCreateSlip) ? "opacity-50 cursor-not-allowed" : ""}`}><PlusIcon size={14} weight="bold" />{isExportMode ? "New Export Packing Slip" : "New Packing Slip"}</button>
        </div>
      </div>

      {/* Bulk-print action bar — surfaces when 1+ slips are selected */}
      {selected.size > 0 && (
        <div data-testid="ps-bulk-bar" className="flex items-center justify-between gap-3 px-3 py-2 rounded-sm border border-[var(--accent)]/40 bg-[var(--accent)]/10">
          <div className="text-xs">
            <span className="font-mono font-semibold text-[var(--accent)]" data-testid="ps-bulk-count">{selected.size}</span>
            <span className="text-zinc-400"> slip{selected.size === 1 ? "" : "s"} selected · will emit </span>
            <span className="font-mono font-semibold text-white">{selected.size * 2}</span>
            <span className="text-zinc-400"> labels (FRONT + BACK)</span>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="ps-bulk-clear" onClick={clearSelection} className="h-8 px-3 border border-[var(--border)] hover:bg-[var(--bg-2)] text-xs rounded-sm">Clear</button>
            <button data-testid="ps-bulk-print" onClick={bulkPrint} className="h-8 px-4 bg-[var(--accent)] hover:bg-blue-600 text-white text-xs font-semibold rounded-sm flex items-center gap-2"><PrinterIcon size={12} weight="bold" />Bulk Print {selected.size * 2} Labels</button>
          </div>
        </div>
      )}

      <div className="tactical-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
              <th className="text-center px-3 py-3 w-10">
                <input
                  type="checkbox"
                  data-testid="ps-select-all"
                  checked={allFilteredSelected}
                  onChange={toggleAllFiltered}
                  className="w-3.5 h-3.5 accent-[var(--accent)] cursor-pointer"
                  title={allFilteredSelected ? "Deselect all visible" : "Select all visible"}
                />
              </th>
              <th className="text-left px-4 py-3 font-medium">Slip No</th>
              <th className="text-left px-4 py-3 font-medium">Date</th>
              <th className="text-left px-4 py-3 font-medium">Order No</th>
              <th className="text-left px-4 py-3 font-medium">Customer</th>
              <th className="text-left px-4 py-3 font-medium">PO No</th>
              <th className="text-left px-4 py-3 font-medium">Product Code</th>
              <th className="text-left px-4 py-3 font-medium">Style No</th>
              <th className="text-right px-4 py-3 font-medium">Meters</th>
              <th className="text-right px-4 py-3 font-medium">Net Wt (kg)</th>
              <th className="text-right px-4 py-3 font-medium">Gross Wt (kg)</th>
              <th className="text-right px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSlips.slice(0, visibleCount).map(s => {
              const b0 = (s.boxes && s.boxes[0]) || {};
              const m = Number(b0.meters) || 0;
              const n = Number(b0.net_weight) || 0;
              const g = Number(b0.gross_weight) || 0;
              return (
                <tr key={s.id} className={`border-b border-[var(--border)] hover:bg-[var(--bg-2)]/40 ${selected.has(s.id) ? "bg-[var(--accent)]/5" : ""}`} data-testid={`ps-row-${s.slip_no}`}>
                  <td className="px-3 py-3 text-center">
                    <input
                      type="checkbox"
                      data-testid={`ps-select-${s.slip_no}`}
                      checked={selected.has(s.id)}
                      onChange={() => toggleSel(s.id)}
                      className="w-3.5 h-3.5 accent-[var(--accent)] cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-3 font-mono font-semibold text-[var(--accent)]">{s.slip_no}</td>
                  <td className="px-4 py-3 font-mono text-zinc-400">{s.pack_date}</td>
                  <td className="px-4 py-3 font-mono">
                    {(s.source_type === "stock") ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-emerald-500/15 text-emerald-400 text-[10px] font-semibold uppercase tracking-wider" data-testid={`ps-stock-badge-${s.slip_no}`}>STOCK</span>
                    ) : (s.source_type === "export_order" || s.export_order_id) ? (
                      <span className="inline-flex items-center gap-1.5" data-testid={`ps-export-badge-${s.slip_no}`}>
                        <span className="px-1.5 py-0.5 rounded-sm bg-sky-500/15 text-sky-400 text-[10px] font-semibold uppercase tracking-wider">EXPORT</span>
                        <OrderRef className="text-xs text-zinc-300" no={exportOrderNo(s.export_order_id)} stock={slipIsStock(s)} testId={`ps-order-ref-${s.slip_no}`} />
                      </span>
                    ) : (
                      <OrderRef no={orderNo(s.order_id)} stock={slipIsStock(s)} testId={`ps-order-ref-${s.slip_no}`} />
                    )}
                    <FgSlipTags s={s} />
                  </td>
                  {/* Feb 2026 · Customer + PO parity with PL / DC tabs. */}
                  <td className="px-4 py-3 text-zinc-300" data-testid={`ps-customer-${s.slip_no}`}>
                    {customerNameFor(s.order_id, s.export_order_id) || <span className="text-zinc-600">—</span>}
                  </td>
                  <td className="px-4 py-3 font-mono text-zinc-400 text-xs" data-testid={`ps-po-${s.slip_no}`}>
                    {buyerPoFor(s.order_id, s.export_order_id) || <span className="text-zinc-600">—</span>}
                  </td>
                  <td className="px-4 py-3 font-mono text-zinc-300 text-xs truncate max-w-[320px]" data-testid={`ps-product-${s.slip_no}`} title={b0.product_code || ""}>{b0.product_code || <span className="text-zinc-600">—</span>}</td>
                  <td className="px-4 py-3 font-mono text-zinc-300 text-xs truncate max-w-[180px]" data-testid={`ps-style-${s.slip_no}`}>{styleNoFor(s.order_id, b0.product_code, b0.style_no, s.export_order_id) || <span className="text-zinc-600">—</span>}</td>
                  <td className="px-4 py-3 text-right font-mono">{m.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>
                  <td className="px-4 py-3 text-right font-mono">{n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="px-4 py-3 text-right font-mono">{g.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <RowActionsMenu testId={`ps-actions-${s.slip_no}`}>
                    {(s.source_type === "stock") ? (
                      <>
                        <button onClick={() => autoAllocate(s)} data-testid={`ps-auto-allocate-${s.slip_no}`} title="Auto Allocate · FIFO-fill matching approved Sales Orders" className="px-2 py-1 rounded-sm bg-blue-500/15 hover:bg-blue-500/25 border border-blue-500/40 text-blue-300 text-[10px] font-medium tracking-wide inline-flex items-center gap-1 mr-1"><MagicWandIcon size={12} weight="bold" />Auto Allocate</button>
                        <button onClick={() => openConvert(s)} data-testid={`ps-convert-${s.slip_no}`} title="Convert to SO Slip · Link this stock slip to a Sales Order" className="p-1.5 rounded-sm hover:bg-emerald-500/10 text-emerald-400"><LinkSimpleIcon size={14} weight="bold" /></button>
                        {(s.allocations || []).length > 0 && !lockedPsIds.has(s.id) && (
                          <button onClick={() => unAllocate(s)} disabled={!rbacReady || !canDeleteSlip} data-testid={`ps-unallocate-${s.slip_no}`} title={rbacReady && !canDeleteSlip ? `🔒 ${slipModule}.delete — ask an admin to grant this` : "Un-Allocate · reverse allocation, delete child slips, restore SO pending"} className={`p-1.5 rounded-sm hover:bg-red-500/10 text-red-400 ml-1 ${(!rbacReady || !canDeleteSlip) ? "opacity-40 cursor-not-allowed" : ""}`}><LinkBreakIcon size={14} weight="bold" /></button>
                        )}
                      </>
                    ) : s.order_id && !lockedPsIds.has(s.id) && !s.created_from_fg ? (
                      <button onClick={() => unlinkToStock(s)} data-testid={`ps-unlink-${s.slip_no}`} title="Unlink from SO · Move this slip back to Stock" className="p-1.5 rounded-sm hover:bg-orange-500/10 text-orange-400"><LinkBreakIcon size={14} weight="bold" /></button>
                    ) : null}
                    <button onClick={() => setPreview(s)} data-testid={`ps-view-${s.slip_no}`} title="View" className="p-1.5 rounded-sm hover:bg-[var(--bg-2)] text-[var(--accent)]"><EyeIcon size={14} /></button>
                    <button onClick={() => printLabels(s)} data-testid={`ps-print-${s.slip_no}`} title="Print Barcode Labels" className="p-1.5 rounded-sm hover:bg-[var(--bg-2)] text-[var(--accent)]"><PrinterIcon size={14} /></button>
                    {lockedPsIds.has(s.id) ? (
                      <span data-testid={`ps-locked-${s.slip_no}`} title={`Packed into ${lockedPsMap[s.id] || "PL"} · edit via Amend on the PL`} className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-amber-500/15 border border-amber-500/40 text-amber-300 text-[10px] font-medium ml-1">
                        <LockIcon size={11} weight="bold" />
                        {lockedPsMap[s.id] || "Locked"}
                      </span>
                    ) : (
                      <>
                        {!s.created_from_fg && <button onClick={() => openDuplicate(s)} data-testid={`ps-dup-${s.slip_no}`} title="Duplicate as Next Box" className="p-1.5 rounded-sm hover:bg-emerald-500/10 text-emerald-400"><CopyIcon size={14} /></button>}
                        {!s.created_from_fg && <button onClick={() => openEdit(s)} disabled={!rbacReady || !canEditSlip} data-testid={`ps-edit-${s.slip_no}`} title={rbacReady && !canEditSlip ? `🔒 ${slipModule}.edit — ask an admin to grant this` : "Edit"} className={`p-1.5 rounded-sm hover:bg-[var(--bg-2)] ${(!rbacReady || !canEditSlip) ? "opacity-40 cursor-not-allowed" : ""}`}><PencilIcon size={14} /></button>}
                        <button onClick={() => remove(s.id)} disabled={!rbacReady || !canDeleteSlip} data-testid={`ps-del-${s.slip_no}`} title={rbacReady && !canDeleteSlip ? `🔒 ${slipModule}.delete — ask an admin to grant this` : "Delete"} className={`p-1.5 rounded-sm hover:bg-red-500/10 text-red-400 ${(!rbacReady || !canDeleteSlip) ? "opacity-40 cursor-not-allowed" : ""}`}><TrashIcon size={14} /></button>
                      </>
                    )}
                    </RowActionsMenu>
                  </td>
                </tr>
              );
            })}
            {filteredSlips.length === 0 && (
              search ? (
                <tr><td colSpan={10} className="text-center py-16 text-zinc-500">No slips match "<span className="text-zinc-300 font-mono">{search}</span>"</td></tr>
              ) : (
                <tr><td colSpan={10} className="text-center py-16 text-zinc-500">No Packing Slips yet — click "New Packing Slip" to create one.</td></tr>
              )
            )}
            {/* Mar 2026 · Truncation Fix — client-side pagination via a
                lightweight "Show More" button keeps the DOM fast even at
                10k+ slips. Backend now returns EVERY row (was capped at
                2000), so the button can page all the way back to slip 001. */}
            {filteredSlips.length > visibleCount && (
              <tr data-testid="ps-show-more-row">
                <td colSpan={12} className="text-center py-4 border-t border-[var(--border)] bg-[var(--bg-2)]/30">
                  <span className="text-xs text-zinc-500 mr-3">
                    Showing <span className="font-mono text-white">{visibleCount}</span> of <span className="font-mono text-white">{filteredSlips.length}</span> slips
                  </span>
                  <button
                    data-testid="ps-show-more"
                    onClick={() => setVisibleCount(c => c + PS_PAGE_SIZE)}
                    className="h-8 px-4 border border-[var(--accent)] rounded-sm text-xs uppercase tracking-widest text-[var(--accent)] hover:bg-[var(--accent)] hover:text-white mr-2"
                  >Show {Math.min(PS_PAGE_SIZE, filteredSlips.length - visibleCount)} more</button>
                  <button
                    data-testid="ps-show-all"
                    onClick={() => setVisibleCount(filteredSlips.length)}
                    className="h-8 px-4 border border-[var(--border)] rounded-sm text-xs uppercase tracking-widest text-zinc-300 hover:border-[var(--accent)]"
                  >Show All ({filteredSlips.length})</button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="ps-dialog" description="Packing / dispatch document action — Packing Slip, Delivery Challan or Packing List.">
          <DialogHeader><DialogTitle className="font-display">{editing ? `Edit ${editing.slip_no}` : (isExportMode ? "New Export Packing Slip" : "New Packing Slip")}</DialogTitle></DialogHeader>

          {/* Feb 27, 2026 · 5-tab Pack Against strip · SO / ESO / Excess vs
              SO / Excess vs ESO / Stock. Excess tabs bypass the Total-Qty
              gate (Admin-approved Over-Production entries only).
              Mar 2026 · Mode-aware — export tab hides SO/Stock/Excess-SO,
              domestic tab hides ESO/Excess-ESO. Same testids so muscle
              memory + regression selectors keep working. */}
          <div className="mb-3 border border-[var(--border)] rounded-sm bg-[var(--bg-2)]/40 p-2 flex items-center gap-2 flex-wrap">
            <span className="overline text-[10px] pl-1 pr-2 text-zinc-500">Pack Against</span>
            {!isExportMode && (
            <button type="button" data-testid="pstab-sales_order"
              onClick={() => { setSourceType("sales_order"); setForm(f => ({ ...f, excess_entry_id: "", export_order_id: "" })); }}
              className={`px-3 py-1.5 rounded-sm text-xs font-semibold tracking-wide transition-colors ${sourceType === "sales_order" ? "bg-[var(--accent)] text-white" : "text-zinc-400 hover:bg-[var(--bg-1)]"}`}
            >SO</button>
            )}
            {!isDomesticMode && (
            <button type="button" data-testid="pstab-export_order"
              onClick={() => { setSourceType("export_order"); setForm(f => ({ ...f, order_id: "", excess_entry_id: "", boxes: f.boxes.map(b => ({ ...b, item_id: "", product_code: "" })) })); }}
              className={`px-3 py-1.5 rounded-sm text-xs font-semibold tracking-wide transition-colors ${sourceType === "export_order" ? "bg-sky-500 text-white" : "text-zinc-400 hover:bg-[var(--bg-1)]"}`}
            >ESO</button>
            )}
            {!isExportMode && (
            <button type="button" data-testid="pstab-excess_so"
              onClick={() => { setSourceType("excess_so"); setForm(f => ({ ...f, export_order_id: "", excess_entry_id: "", boxes: f.boxes.map(b => ({ ...b, item_id: "", product_code: "" })) })); }}
              title="Pack against Over-Production entries that have been Approved for Billing"
              className={`px-3 py-1.5 rounded-sm text-xs font-semibold tracking-wide transition-colors ${sourceType === "excess_so" ? "bg-amber-500 text-black" : "text-amber-300 hover:bg-amber-500/10 border border-amber-500/40"}`}
            >Excess vs SO</button>
            )}
            {!isDomesticMode && (
            <button type="button" data-testid="pstab-excess_eso"
              onClick={() => { setSourceType("excess_eso"); setForm(f => ({ ...f, order_id: "", excess_entry_id: "", boxes: f.boxes.map(b => ({ ...b, item_id: "", product_code: "" })) })); }}
              title="Pack against Over-Production entries (ESO) that have been Approved for Billing"
              className={`px-3 py-1.5 rounded-sm text-xs font-semibold tracking-wide transition-colors ${sourceType === "excess_eso" ? "bg-amber-500 text-black" : "text-amber-300 hover:bg-amber-500/10 border border-amber-500/40"}`}
            >Excess vs ESO</button>
            )}
            <button type="button" data-testid="pstab-stock"
              onClick={() => { setSourceType("stock"); setForm(f => ({ ...f, order_id: "", export_order_id: "", stock_order_id: "", stock_line_id: "", boxes: f.boxes.map(b => ({ ...b, item_id: "", product_code: "" })) })); }}
              className={`px-3 py-1.5 rounded-sm text-xs font-semibold tracking-wide transition-colors ${sourceType === "stock" ? "bg-emerald-500 text-white" : "text-zinc-400 hover:bg-[var(--bg-1)]"}`}
            >Stock</button>
            <span className="text-[10px] text-zinc-500 ml-2 italic">
              {sourceType === "sales_order"    ? "Strict Total-Qty gate · box code restricted to the SO's lines"
             : sourceType === "export_order"    ? "Strict Total-Qty gate · box code restricted to the ESO's lines"
             : sourceType === "excess_so"       ? "Approved Over-Production only · Total-Qty gate bypassed · tagged Excess Dispatch"
             : sourceType === "excess_eso"      ? "Approved Over-Production (ESO) only · gate bypassed · tagged Excess Dispatch"
             : `Stock ${isExportMode ? "ESO" : "SO"} + exact line · FG receipt via Packing → FG Store transfer`}
            </span>
          </div>

          {/* Feb 27, 2026 · Approved excess-lot picker inline in the
              single-slip dialog for the two Excess tabs. Picking a lot
              caps subsequent boxes on that line at its `excess_qty`. */}
          {(sourceType === "excess_so" || sourceType === "excess_eso") && (
            <div className="mb-3 border border-amber-500/40 rounded-sm bg-amber-500/5 p-3" data-testid="ps-excess-picker">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] font-mono uppercase tracking-widest text-amber-300">Pack-First · {sourceType === "excess_so" ? "Sales Order" : "Export Sales Order"} · Optional Lot Link</div>
                {selectedExcessLot && (() => {
                  const cap = Number(selectedExcessLot.excess_qty) || 0;
                  const used = (form.boxes || []).reduce((s, b) => s + (Number(b.meters) || 0), 0);
                  const rem = Math.max(0, cap - used);
                  return (
                    <div className="text-[10px] font-mono flex items-center gap-2" data-testid="ps-excess-capacity">
                      <span className="text-zinc-400">Used <span className="text-white">{used.toFixed(2)}</span> / <span className="text-emerald-300">{cap}</span> {selectedExcessLot.unit || ""}</span>
                      <span className={`px-1.5 py-0.5 rounded-sm ${rem <= 0 ? "bg-red-500/20 text-red-300" : "bg-emerald-500/20 text-emerald-300"}`}>{rem.toFixed(2)} remaining</span>
                    </div>
                  );
                })()}
              </div>
              {/* Feb 28, 2026 · Selected-lot summary strip — surfaces the exact
                  SO / ESO reference number, line item + style, and fabric type
                  so the packer knows what they're dispatching against. */}
              {selectedExcessLot && (
                <div className="mb-2 p-2 rounded-sm border border-amber-500/60 bg-[var(--bg-0)]/60" data-testid="ps-excess-selected-summary">
                  <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-[11px] font-mono">
                    <span className="text-[9px] uppercase tracking-widest text-amber-400">Packing Against</span>
                    <span className="text-white"><span className="text-zinc-500">{sourceType === "excess_so" ? "SO" : "ESO"}:</span> <span className="text-amber-300 font-semibold">{selectedExcessLot.order_no || "—"}</span></span>
                    <span className="text-white"><span className="text-zinc-500">Line:</span> <span className="text-zinc-200 truncate max-w-[280px] inline-block align-bottom">{selectedExcessLot.article_no || selectedExcessLot.product_code || "—"}</span></span>
                    {selectedExcessLot.style_no && <span className="text-white"><span className="text-zinc-500">Style:</span> <span className="text-zinc-200">{selectedExcessLot.style_no}</span></span>}
                    <span className="text-white"><span className="text-zinc-500">Fabric:</span> <span className="text-zinc-200">{selectedExcessLot.fabric_type || "—"}</span></span>
                    <span className="text-white"><span className="text-zinc-500">Entry:</span> <span className="text-zinc-200">{selectedExcessLot.entry_no}</span></span>
                    <span className="text-white"><span className="text-zinc-500">Approved Qty:</span> <span className="text-emerald-300">{selectedExcessLot.excess_qty} {selectedExcessLot.unit || ""}</span></span>
                  </div>
                </div>
              )}
              {approvedLotsLoading ? <div className="text-xs text-zinc-500">Loading approved lots…</div>
              : approvedLots.length === 0 ? (
                <div className="text-xs" data-testid="ps-excess-empty">
                  <div className="text-zinc-400 mb-1.5"><span className="text-amber-300 font-semibold">Pack-First flow</span> — pack the excess now; the slip auto-posts to the Excess Production Queue for approval.</div>
                  <div className="text-[11px] text-zinc-500 mb-2 leading-relaxed">
                    No approved lots on file yet. Save the slip below to create a Pending Approval entry, then an Admin approves it in the queue before it can be included in a Packing List.
                  </div>
                  <a href="/dispatch/excess-production" data-testid="ps-excess-empty-cta" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/60 text-amber-200 text-[11px] font-semibold">
                    Open Excess Production Queue
                    {pendingExcessCount > 0 && <span className="px-1.5 py-0.5 rounded-sm bg-amber-500/40 text-white font-mono">{pendingExcessCount} pending</span>}
                    <span aria-hidden="true">→</span>
                  </a>
                </div>
              )
              : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                  {approvedLots.map(l => (
                    <button key={l.id} type="button" data-testid={`ps-excess-lot-${l.entry_no}`}
                      onClick={() => {
                        if (sourceType === "excess_so") setForm(f => ({ ...f, order_id: l.order_id || f.order_id, excess_entry_id: l.id, boxes: f.boxes.map(b => ({ ...b, product_code: l.product_code || b.product_code })) }));
                        else                             setForm(f => ({ ...f, export_order_id: l.order_id || f.export_order_id, excess_entry_id: l.id, boxes: f.boxes.map(b => ({ ...b, product_code: l.product_code || b.product_code })) }));
                      }}
                      className={`text-left p-2 rounded-sm border text-xs ${form.excess_entry_id === l.id ? "border-amber-400 bg-amber-500/10" : "border-[var(--border)] hover:border-amber-500/40"}`}>
                      <div className="flex items-center justify-between font-mono">
                        <span className="text-amber-300 font-semibold">{l.entry_no}</span>
                        <span className="text-emerald-300 font-semibold">{l.excess_qty} {l.unit}</span>
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-[10px]">
                        <span className="px-1.5 py-0.5 rounded-sm bg-blue-500/15 border border-blue-500/40 text-blue-300 font-mono font-semibold">
                          {sourceType === "excess_so" ? "SO" : "ESO"} · {l.order_no || "—"}
                        </span>
                        {l.style_no && <span className="text-zinc-400">Style <span className="text-zinc-200 font-mono">{l.style_no}</span></span>}
                      </div>
                      <div className="text-[10px] text-zinc-400 truncate mt-0.5" title={l.article_no || l.product_code}>
                        <span className="text-zinc-500">Line: </span>{l.article_no || l.product_code}
                      </div>
                      <div className="text-[9px] text-zinc-500 truncate">{l.fabric_type || "—"} · from slip {l.slip_no}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-3 gap-3 mb-3">
            {sourceType === "sales_order" ? (
              <div>
                <div className="overline mb-1.5">Sales Order</div>
                <Select value={form.order_id} onValueChange={onOrderChange}>
                  <SelectTrigger data-testid="ps-order" className={selCls}><SelectValue placeholder="Pick a Sales Order…" /></SelectTrigger>
                  <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                    {orders.filter(o => fulfilmentOf(o) !== "sales_only").map(o => <SelectItem key={o.id} value={o.id}>{o.order_no}{isStockOrder(o) ? " · STOCK" : ""} — {customerNameFor(o.id) || "—"}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : sourceType === "export_order" ? (
              <div>
                <div className="overline mb-1.5">Export Sales Order</div>
                <Select value={form.export_order_id} onValueChange={onExportOrderChange}>
                  <SelectTrigger data-testid="ps-eso" className={selCls}><SelectValue placeholder="Pick an Export SO…" /></SelectTrigger>
                  <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                    {exportOrders.filter(o => fulfilmentOf(o) !== "sales_only").map(o => <SelectItem key={o.id} value={o.id} data-testid={`ps-eso-opt-${o.doc_no}`}>{o.doc_no}{isStockOrder(o) ? " · STOCK" : ""} — {o.buyer_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <StockPackPicker prefix="ps" orders={isExportMode ? exportOrders : orders} isExport={isExportMode} selCls={selCls} errOrder={errors["form.stock_order_id"]} errLine={errors["form.stock_line_id"]}
                orderId={form.stock_order_id} lineId={form.stock_line_id}
                onOrder={(v) => setForm(f => ({ ...f, stock_order_id: v, stock_line_id: "", boxes: f.boxes.map(b => ({ ...b, item_id: "", product_code: "" })) }))}
                onLine={(v) => setForm(f => {
                  const so = (isExportMode ? exportOrders : orders).find(o => o.id === f.stock_order_id);
                  const l = stockLinesOf(so, isExportMode).find(x => x.line_id === v);
                  return { ...f, stock_line_id: v, boxes: f.boxes.map(b => ({ ...b, item_id: l ? (l.item_id || l.code) : "", product_code: l ? l.code : "", style_no: l?.style_no || b.style_no })) };
                })} />
            )}
            <div>
              <div className="overline mb-1.5">Pack Date</div>
              <input type="date" data-testid="ps-date" value={form.pack_date} onChange={e => setForm(f => ({ ...f, pack_date: e.target.value }))} className={inp} />
            </div>
            <div>
              <div className="overline mb-1.5">Remarks</div>
              <input value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} className={inp} />
            </div>
          </div>

          <div className="flex items-center justify-between mb-2 gap-3">
            <div className="overline">Box · Auto-numbered on save</div>
            {/* Style-No filter (Feb 2026) — narrows the Product Code dropdown
                to just the SO line items whose Style No matches. Handy on
                multi-style SOs where the packer knows the customer style but
                not the internal article code. Auto-clears on dialog open. */}
            {sourceType === "sales_order" && form.order_id && allowedItems.some(it => it.style_no) && (
              <div className="flex items-center gap-2">
                <label className="text-[10px] uppercase tracking-widest text-zinc-500">Filter by Style</label>
                <input
                  data-testid="ps-style-filter"
                  value={styleFilter}
                  onChange={e => setStyleFilter(e.target.value)}
                  placeholder="e.g. STY-2026-A47"
                  className={`${inp} w-56 font-mono text-xs`}
                />
                {styleFilter && (
                  <button
                    data-testid="ps-style-filter-clear"
                    onClick={() => setStyleFilter("")}
                    className="h-9 px-2 border border-[var(--border)] hover:bg-[var(--bg-2)] text-[10px] uppercase tracking-widest text-zinc-400 rounded-sm"
                    title="Clear the Style filter"
                  >Clear</button>
                )}
                <span className="text-[10px] font-mono text-zinc-500 whitespace-nowrap" data-testid="ps-style-filter-count">
                  {filteredAllowedItems.length}/{allowedItems.length}
                </span>
              </div>
            )}
          </div>
          <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-[var(--bg-2)] uppercase tracking-widest text-[10px] text-zinc-500">
                  <th className="px-2 py-2 text-left w-16">Box No</th>
                  <th className="px-2 py-2 text-left">Product Code</th>
                  <th className="px-2 py-2 text-left w-40">Style No</th>
                  <th className="px-2 py-2 text-right w-24">Meters</th>
                  <th className="px-2 py-2 text-right w-20">Joints</th>
                  <th className="px-2 py-2 text-right w-24">Net Wt</th>
                  <th className="px-2 py-2 text-right w-24">Tare Wt</th>
                  <th className="px-2 py-2 text-right w-24 bg-emerald-500/10">Gross Wt</th>
                </tr>
              </thead>
              <tbody>
                {form.boxes.map((b, i) => (
                  <tr key={i} className="border-t border-[var(--border)]" data-testid={`ps-box-row-${i}`}>
                    <td className="px-2 py-1 font-mono text-zinc-500 text-[10px]">B{String(i + 1).padStart(2, "0")}</td>
                    <td className="px-1 py-1">
                      <Select value={b.item_id} onValueChange={v => {
                        const it = allowedItems.find(x => x.id === v) || items.find(x => x.id === v) || { code: v };
                        setForm(f => ({ ...f, boxes: f.boxes.map((row, x) => x === i ? { ...row, item_id: v, product_code: it.code || row.product_code, style_no: it.style_no || row.style_no || "" } : row) }));
                      }}>
                        <SelectTrigger data-testid={`ps-box-product-${i}`} className={selCls}>
                          <SelectValue placeholder={form.order_id ? (allowedItems.length ? "Select product from SO" : "SO has no line articles") : (b.product_code || "Select product")}>
                            {b.product_code || undefined}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                          {filteredAllowedItems.length === 0 && styleFilter && (
                            <div className="px-3 py-2 text-[11px] text-amber-400" data-testid="ps-style-filter-empty">
                              No articles on this SO match style "<span className="font-mono">{styleFilter}</span>"
                            </div>
                          )}
                          {filteredAllowedItems.map(it => (
                            <SelectItem key={it.id} value={it.id} data-testid={`ps-box-product-opt-${it.code}`}>
                              <span className="font-mono">{it.code}</span>
                              {it.style_no && <span className="text-zinc-500 ml-2">· {it.style_no}</span>}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    {/* Style No — auto-derived from the SO's line-item matching
                        the picked Product Code. Read-only column (Feb 2026);
                        renders `—` for Stock-mode slips or when the SO line
                        item has no `style_no` set. */}
                    <td className="px-2 py-1 font-mono text-[11px]" data-testid={`ps-box-style-${i}`}>
                      {(() => {
                        const sn = styleNoFor(form.order_id, b.product_code, b.style_no, form.export_order_id);
                        return sn ? <span className="text-zinc-200">{sn}</span> : <span className="text-zinc-600">—</span>;
                      })()}
                    </td>
                    <td className="px-1 py-1"><FormattedNumberInput step="0.01" data-testid={`ps-box-meters-${i}`} value={b.meters} onChange={e => updateBox(i, "meters", e.target.value)} className={inpNum} /></td>
                    <td className="px-1 py-1"><FormattedNumberInput data-testid={`ps-box-joints-${i}`} value={b.joints} onChange={e => updateBox(i, "joints", e.target.value)} className={inpNum} /></td>
                    <td className="px-1 py-1"><FormattedNumberInput step="0.01" data-testid={`ps-box-net-${i}`} value={b.net_weight} onChange={e => updateBox(i, "net_weight", e.target.value)} className={inpNum} /></td>
                    <td className="px-1 py-1"><FormattedNumberInput step="0.01" data-testid={`ps-box-tare-${i}`} value={b.tare_weight} onChange={e => updateBox(i, "tare_weight", e.target.value)} className={inpNum} /></td>
                    <td className="px-1 py-1">
                      {/* Feb 2026 · Gross Wt is a derived value = Net + Tare.
                          Locked to read-only so operators can't punch in an
                          inconsistent number that would fail downstream
                          weight reconciliation. */}
                      <div
                        data-testid={`ps-box-gross-${i}`}
                        title="Auto = Net + Tare (locked)"
                        className={inpNum + " bg-emerald-500/10 border-emerald-500/30 text-emerald-400 font-semibold flex items-center justify-end cursor-not-allowed"}
                      >
                        {((Number(b.net_weight) || 0) + (Number(b.tare_weight) || 0)).toFixed(2)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400">Cancel</button>
            {/* Mar 2026 · Save Draft escape hatch — skips validation so operators can park an unfinished slip. */}
            <button data-testid="ps-save-draft" onClick={() => save(true)} className="px-4 py-2 rounded-sm border border-amber-500/60 text-sm text-amber-300 hover:bg-amber-500/10 font-semibold">Save Draft</button>
            <button data-testid="ps-save" onClick={() => save(false)} className="px-4 py-2 rounded-sm bg-[var(--accent)] text-sm font-semibold text-white">Save Packing Slip</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View / Barcode preview dialog — renders each box's sticker at the
          CURRENTLY-SELECTED preset size (100×75 mm, 4×6, etc.) so what the
          operator sees on-screen mirrors what the printer will emit. */}
      {/* Feb 2026 · Allocation History dialog — flat ledger of every
          parent → child → SO allocation. Loaded on demand via the History
          button in the page header. Read-only audit view. */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="alloc-history-dialog" description="Packing / dispatch document action — Packing Slip, Delivery Challan or Packing List.">
          <DialogHeader>
            <DialogTitle className="font-display">Allocation History · {historyRows.length} entries</DialogTitle>
          </DialogHeader>
          <div className="border border-[var(--border)] rounded-sm overflow-auto max-h-[70vh]">
            <table className="w-full text-xs">
              <thead className="bg-[var(--bg-2)]/60 sticky top-0">
                <tr className="text-[10px] uppercase tracking-widest text-zinc-500">
                  <th className="text-left px-2 py-2">Parent Slip</th>
                  <th className="text-left px-2 py-2">Child Slip</th>
                  <th className="text-left px-2 py-2">Sales Order</th>
                  <th className="text-right px-2 py-2">Allocated Qty</th>
                  <th className="text-left px-2 py-2">By</th>
                  <th className="text-left px-2 py-2">At</th>
                </tr>
              </thead>
              <tbody data-testid="alloc-history-tbody">
                {historyRows.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-6 text-zinc-500">No allocations yet.</td></tr>
                )}
                {historyRows.map((r, i) => (
                  <tr key={i} className="border-t border-[var(--border)]">
                    <td className="px-2 py-1 font-mono text-zinc-300">{r.parent_slip_no}</td>
                    <td className="px-2 py-1 font-mono text-emerald-300">{r.child_slip_no}</td>
                    <td className="px-2 py-1 font-mono text-blue-300">{r.so_no || "—"}</td>
                    <td className="px-2 py-1 text-right font-mono text-white">{Number(r.allocated_qty || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="px-2 py-1 text-zinc-400 text-[10px]">{r.allocated_by || "—"}</td>
                    <td className="px-2 py-1 text-zinc-500 text-[10px]">{fmtDateTimeIST(r.allocated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>


      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="ps-view-dialog" description="Packing / dispatch document action — Packing Slip, Delivery Challan or Packing List.">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <BarcodeIcon size={20} weight="bold" className="text-[var(--accent)]" />
              <span>{preview?.slip_no}</span>
              <span className="text-xs text-zinc-500 font-normal" data-testid="ps-preview-header-ref">· {preview ? slipRefText(preview) : ""} · {preview?.pack_date}</span>
              {/* Feb 2026 · Auto Allocate shortcut inside the view dialog
                  (stock slips only — SO-linked slips are already allocated).
                  Duplicates the row-level button so users who deep-dive into
                  the slip preview don't have to close and re-open. */}
              {preview && preview.source_type === "stock" && (
                <button
                  data-testid={`ps-auto-allocate-dialog-${preview.slip_no}`}
                  onClick={() => { autoAllocate(preview); setPreview(null); }}
                  title="Auto Allocate · FIFO-fill matching approved Sales Orders"
                  className="ml-auto px-3 py-1.5 rounded-sm bg-blue-500/15 hover:bg-blue-500/25 border border-blue-500/40 text-blue-300 text-xs font-medium tracking-wide inline-flex items-center gap-1.5"
                ><MagicWandIcon size={13} weight="bold" />Auto Allocate</button>
              )}
            </DialogTitle>
          </DialogHeader>
          {preview && (() => {
            // Effective preset for THIS slip. Defaults to the global toolbar
            // selection; per-slip override kicks in when the picker in the
            // dialog header is changed. `activeKey` also feeds the print flow
            // (via `printLabels(preview, activeKey)`) so what the operator
            // sees on screen == what the printer emits.
            const activeKey = (viewPreset && LABEL_PRESETS[viewPreset]) ? viewPreset : labelPreset;
            const P = LABEL_PRESETS[activeKey] || LABEL_PRESETS["4x6"];
            const sides = P.paired ? ["FRONT", "BACK"] : ["LABEL"];
            const po = buyerPoFor(preview.order_id, preview.export_order_id);
            return (
              <>
                <DocumentInfo docNo={preview.slip_no} createdAt={preview.created_at} updatedAt={preview.updated_at} createdBy={preview.created_by} docLabel="Packing Slip No." testidPrefix="ps" />
                <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
                  <div className="text-[10px] text-zinc-500 uppercase tracking-widest">
                    Paper size · <span className="text-zinc-300 font-mono normal-case tracking-normal">{P.pageW} × {P.pageH}</span>{" · "}
                    <span className="text-zinc-300 normal-case tracking-normal">{P.label}</span>{" · "}
                    {P.paired ? "FRONT + BACK per box" : "Single sticker per box"}{" · TSC TE244 compatible"}
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Per-slip preset override — defaults to the global setting;
                        picking a different size affects only this slip's on-screen
                        preview + Print click. `viewPreset` auto-clears when the
                        dialog closes so the override never leaks. */}
                    <Select value={activeKey} onValueChange={(v) => setViewPreset(v === labelPreset ? null : v)}>
                      <SelectTrigger data-testid="ps-view-preset" className="h-9 w-52 bg-[var(--bg-0)] border-[var(--border)] text-xs text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                        {Object.entries(LABEL_PRESETS).map(([k, v]) => (
                          <SelectItem key={k} value={k} data-testid={`ps-view-preset-${k}`}>
                            {v.label}{k === labelPreset ? "  ·  Global" : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {viewPreset && (
                      <button
                        data-testid="ps-view-preset-reset"
                        onClick={() => setViewPreset(null)}
                        title="Revert to the toolbar preset"
                        className="h-9 px-2 border border-[var(--border)] hover:bg-[var(--bg-2)] text-[10px] uppercase tracking-widest text-zinc-400 rounded-sm"
                      >Reset</button>
                    )}
                    <button onClick={() => printLabels(preview, activeKey)} data-testid="ps-print-labels" className="h-9 px-4 bg-[var(--accent)] text-white text-sm font-semibold rounded-sm flex items-center gap-2"><PrinterIcon size={14} weight="bold" />Print Barcode Labels</button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-6 justify-center">
                  {(preview.boxes || []).map(b => (
                    <div
                      key={b.box_no}
                      className="border border-[var(--border)] rounded-sm bg-white text-black font-sans"
                      data-testid={`ps-label-${b.box_no}`}
                      style={{ width: P.pageW, height: P.pageH, boxSizing: "border-box", display: "flex", flexDirection: "column" }}
                    >
                      {sides.map((side, si) => (
                        <div
                          key={side}
                          data-testid={`ps-sticker-${b.box_no}-${side}`}
                          style={{
                            width: P.pageW,
                            height: P.stickerH,
                            padding: P.padding,
                            boxSizing: "border-box",
                            borderTop: P.paired && si === 1 ? "1px dashed #999" : "none",
                            overflow: "hidden",
                            fontWeight: 700,
                            position: "relative",
                          }}
                        >
                          <div style={{ fontSize: P.boxFont, fontWeight: 900, letterSpacing: 1, textAlign: "center", lineHeight: 1, margin: activeKey === "2x1" ? "0.5mm 0 0.2mm 0" : "1.5mm 0 0.2mm 0" }}>
                            {b.box_no}
                          </div>
                          {P.showAll ? (
                            <div style={{ fontSize: `calc(${P.rowFont} * 0.5)`, letterSpacing: "0.3px", textTransform: "uppercase", textAlign: "center", opacity: 0.75, margin: "0.4mm 0 0.8mm 0" }}>
                              {preview.slip_no} · {preview.pack_date}{P.paired ? <> · <span style={{ fontWeight: 900, letterSpacing: 2 }}>{side}</span></> : null}
                            </div>
                          ) : (P.paired || activeKey === "4x2") ? (
                            <div style={{ fontSize: P.hdrFont, letterSpacing: "0.3px", textTransform: "uppercase", fontWeight: 700, opacity: 0.85 }}>
                              {preview.slip_no} · {preview.pack_date}{P.paired ? <> · <span style={{ fontWeight: 900, letterSpacing: 2 }}>{side}</span></> : null}
                            </div>
                          ) : null}
                          {P.showAll && customerNameFor(preview.order_id, preview.export_order_id) && (
                            <div style={{ fontSize: P.rowFont, margin: "0.6mm 0", fontWeight: 800, letterSpacing: "0.2px" }}>
                              <b>Customer:</b> {customerNameFor(preview.order_id, preview.export_order_id)}
                            </div>
                          )}
                          {P.showAll && (styleNoFor(preview.order_id, b.product_code, b.style_no, preview.export_order_id) || po) && (
                            <div style={{ fontSize: P.rowFont, margin: "0.6mm 0", fontWeight: 800, letterSpacing: "0.3px" }}>
                              {styleNoFor(preview.order_id, b.product_code, b.style_no, preview.export_order_id) && <><b>Style:</b> {styleNoFor(preview.order_id, b.product_code, b.style_no, preview.export_order_id)}</>}
                              {styleNoFor(preview.order_id, b.product_code, b.style_no, preview.export_order_id) && po && "   ·   "}
                              {po && <><b>PO:</b> {po}</>}
                            </div>
                          )}
                          {P.showAll && (preview.order_id || preview.export_order_id) && (
                            <div style={{ fontSize: P.rowFont, margin: "0.6mm 0", fontWeight: 700 }}>
                              <b>{preview.export_order_id ? "ESO" : "SO"}:</b> {slipRefText(preview)}
                            </div>
                          )}
                          {(P.showAll || activeKey === "4x2") && (
                            <div style={{ fontSize: P.rowFont, margin: "0.6mm 0", fontWeight: 700 }}><b>Product:</b> {b.product_code}</div>
                          )}
                          {P.showAll && (
                            <>
                              <div style={{ fontSize: P.rowFont, margin: "0.6mm 0", fontWeight: 700 }}><b>Meters:</b> {b.meters} m · <b>Joints:</b> {b.joints}</div>
                              <div style={{ fontSize: P.rowFont, margin: "0.6mm 0", fontWeight: 700 }}><b>Net:</b> {fmtQty(b.net_weight || 0, "KG")} · <b>Tare:</b> {fmtQty(b.tare_weight || 0, "KG")} · <b>Gross:</b> {fmtQty(b.gross_weight || 0, "KG")} kg</div>
                            </>
                          )}
                          <div style={{ display: "block", margin: activeKey === "2x1" ? "0.5mm auto 0 auto" : "2mm auto 0 auto", width: P.bcW, height: P.bcH, background: "#fff", textAlign: "center" }}>
                            <Barcode
                              value={b.box_no}
                              format="CODE128"
                              height={P.bcSvgHeight}
                              fontSize={P.bcTextSize}
                              displayValue={activeKey !== "2x1"}
                              width={activeKey === "2x1" ? 1.5 : 2}
                              margin={0}
                            />
                          </div>
                          {P.showAll && (
                            <div title={`${preview.slip_no}|${b.box_no}|${b.line_id || ""}`}
                              style={{ position: "absolute", bottom: P.padding, right: P.padding, width: "14mm", height: "14mm",
                                       border: "1px dashed #666", display: "flex", alignItems: "center", justifyContent: "center",
                                       fontSize: "6pt", color: "#666", fontFamily: "monospace", background: "#fff" }}>
                              QR
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Live 1:1 label preview (Feb 2026) — picks the first available slip's
          first box as the sample (falls back to synthetic data) and renders
          the sticker at physical size using the SAME @page geometry the print
          flow will emit. Lets operators validate a new media size like 100×75
          without wasting a print run. */}
      <Dialog open={previewLabelOpen} onOpenChange={setPreviewLabelOpen}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="ps-preview-label-dialog" description="Packing / dispatch document action — Packing Slip, Delivery Challan or Packing List.">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <EyeIcon size={20} weight="bold" className="text-[var(--accent)]" />
              <span>Live Label Preview</span>
              <span className="text-xs text-zinc-500 font-normal">· {LABEL_PRESETS[labelPreset]?.label} · 1:1 scale</span>
            </DialogTitle>
          </DialogHeader>
          {(() => {
            const P = LABEL_PRESETS[labelPreset] || LABEL_PRESETS["4x6"];
            const sampleSlip = filteredSlips.find(s => (s.boxes || []).length > 0) || null;
            const sampleBox = sampleSlip ? sampleSlip.boxes[0] : null;
            // Synthetic fallback so the preview still works on a fresh install.
            const slip = sampleSlip || {
              slip_no: "PS-26-001",
              pack_date: new Date().toISOString().slice(0, 10),
              order_id: "",
            };
            const box = sampleBox || {
              box_no: "PS-26-001-B01",
              product_code: "RNJ/38/10547/02",
              meters: 500,
              joints: 2,
              net_weight: 4.25,
              tare_weight: 0.35,
              gross_weight: 4.60,
            };
            const po = buyerPoFor(slip.order_id, slip.export_order_id);
            const styleNo = sampleSlip ? styleNoFor(slip.order_id, box.product_code, box.style_no, slip.export_order_id) : "STY-2026-A47";
            const custName = sampleSlip ? customerNameFor(slip.order_id, slip.export_order_id) : "SAMPLE BUYER LTD";
            const sides = P.paired ? ["FRONT", "BACK"] : ["LABEL"];
            const Sticker = ({ side }) => (
              <div
                data-testid={`ps-preview-sticker-${side}`}
                className="bg-white text-black font-sans"
                style={{
                  width: P.pageW,
                  height: P.stickerH,
                  padding: P.padding,
                  boxSizing: "border-box",
                  borderBottom: P.paired && side === "FRONT" ? "1px dashed #999" : "none",
                  overflow: "hidden",
                  fontWeight: 700,
                  position: "relative",
                }}
              >
                <div style={{ fontSize: P.boxFont, fontWeight: 900, letterSpacing: 1, textAlign: "center", lineHeight: 1, margin: labelPreset === "2x1" ? "0.5mm 0 0.2mm 0" : "1.5mm 0 0.2mm 0" }}>
                  {box.box_no}
                </div>
                {P.showAll ? (
                  <div style={{ fontSize: `calc(${P.rowFont} * 0.5)`, letterSpacing: "0.3px", textTransform: "uppercase", textAlign: "center", opacity: 0.75, margin: "0.4mm 0 0.8mm 0" }}>
                    {slip.slip_no} · {slip.pack_date}{P.paired ? <> · <span style={{ fontWeight: 900, letterSpacing: 2 }}>{side}</span></> : null}
                  </div>
                ) : (P.paired || labelPreset === "4x2") ? (
                  <div style={{ fontSize: P.hdrFont, letterSpacing: "0.3px", textTransform: "uppercase", fontWeight: 700, opacity: 0.85 }}>
                    {slip.slip_no} · {slip.pack_date}{P.paired ? <> · <span style={{ fontWeight: 900, letterSpacing: 2 }}>{side}</span></> : null}
                  </div>
                ) : null}
                {P.showAll && custName && (
                  <div style={{ fontSize: P.rowFont, margin: "0.6mm 0", fontWeight: 800, letterSpacing: "0.2px" }}>
                    <b>Customer:</b> {custName}
                  </div>
                )}
                {P.showAll && (styleNo || po) && (
                  <div style={{ fontSize: P.rowFont, margin: "0.6mm 0", fontWeight: 800, letterSpacing: "0.3px" }}>
                    {styleNo && <><b>Style:</b> {styleNo}</>}
                    {styleNo && po && "   ·   "}
                    {po && <><b>PO:</b> {po}</>}
                  </div>
                )}
                {P.showAll && (slip.order_id || slip.export_order_id) && (
                  <div style={{ fontSize: P.rowFont, margin: "0.6mm 0", fontWeight: 700 }}>
                    <b>{slip.export_order_id ? "ESO" : "SO"}:</b> {slipRefText(slip)}
                  </div>
                )}
                {(P.showAll || labelPreset === "4x2") && (
                  <div style={{ fontSize: P.rowFont, margin: "0.6mm 0", fontWeight: 700 }}><b>Product:</b> {box.product_code}</div>
                )}
                {P.showAll && (
                  <>
                    <div style={{ fontSize: P.rowFont, margin: "0.6mm 0", fontWeight: 700 }}><b>Meters:</b> {box.meters} m · <b>Joints:</b> {box.joints}</div>
                    <div style={{ fontSize: P.rowFont, margin: "0.6mm 0", fontWeight: 700 }}><b>Net:</b> {fmtQty(box.net_weight || 0, "KG")} · <b>Tare:</b> {fmtQty(box.tare_weight || 0, "KG")} · <b>Gross:</b> {fmtQty(box.gross_weight || 0, "KG")} kg</div>
                  </>
                )}
                <div style={{ display: "block", margin: labelPreset === "2x1" ? "0.5mm auto 0 auto" : "2mm auto 0 auto", width: P.bcW, height: P.bcH, background: "#fff", textAlign: "center" }}>
                  <Barcode
                    value={box.box_no}
                    format="CODE128"
                    height={P.bcSvgHeight}
                    fontSize={P.bcTextSize}
                    displayValue={labelPreset !== "2x1"}
                    width={labelPreset === "2x1" ? 1.5 : 2}
                    margin={0}
                  />
                </div>
                {P.showAll && (
                  <div title={`${slip.slip_no}|${box.box_no}|${box.line_id || ""}`}
                    style={{ position: "absolute", bottom: P.padding, right: P.padding, width: "14mm", height: "14mm",
                             border: "1px dashed #666", display: "flex", alignItems: "center", justifyContent: "center",
                             fontSize: "6pt", color: "#666", fontFamily: "monospace", background: "#fff" }}>
                    QR
                  </div>
                )}
              </div>
            );
            return (
              <>
                <div className="text-[10px] text-zinc-500 mb-3 uppercase tracking-widest">
                  Physical size · <span className="text-zinc-300 font-mono normal-case tracking-normal">{P.pageW} × {P.pageH}</span>{" · "}
                  {P.paired ? "Paired FRONT + BACK" : "Single sticker"}{" · "}
                  Sample: {sampleSlip ? <span className="text-zinc-300 font-mono normal-case tracking-normal">{slip.slip_no}</span> : <span className="text-amber-400 normal-case tracking-normal">synthetic (no packing slips yet)</span>}
                </div>
                <div className="flex justify-center">
                  <div
                    data-testid="ps-preview-canvas"
                    className="shadow-2xl"
                    style={{
                      width: P.pageW,
                      height: P.pageH,
                      background: "#fff",
                      boxSizing: "border-box",
                      display: "flex",
                      flexDirection: "column",
                      border: "1px solid #333",
                    }}
                  >
                    {sides.map(s => <Sticker key={s} side={s} />)}
                  </div>
                </div>
                <div className="text-[10px] text-zinc-500 mt-3 text-center italic">
                  Rendered at 1:1 physical scale. Screen DPI may vary — hold a ruler up to the on-screen sticker to confirm the media fit before printing.
                </div>
              </>
            );
          })()}
          <DialogFooter>
            <button onClick={() => setPreviewLabelOpen(false)} className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400">Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Packing Slip generator (Feb 2026) — Customer → SO → Article → N boxes.
          Creates N slips (one box each) with identical pack/weight profile in a
          single API round-trip. Slip numbers ship in a contiguous, sequential run
          so warehouse pickers can stack labels in printed order. */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="ps-bulk-dialog" description="Packing / dispatch document action — Packing Slip, Delivery Challan or Packing List.">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <StackIcon size={20} weight="bold" className="text-emerald-400" />
              Bulk Packing Slips
              <span className="text-xs text-zinc-500 font-normal">· one article · N identically-packed boxes</span>
            </DialogTitle>
          </DialogHeader>

          {/* Feb 27, 2026 · 5-tab Pack Against strip · mirrors the single-slip
              dialog. Excess tabs bypass the Total-Qty gate and stamp
              is_excess_dispatch=true so the dispatch ledger can filter. */}
          <div className="mb-3 border border-[var(--border)] rounded-sm bg-[var(--bg-2)]/40 p-2 flex items-center gap-2 flex-wrap">
            <span className="overline text-[10px] pl-1 pr-2 text-zinc-500">Pack Against</span>
            {!isExportMode && (
            <button type="button" data-testid="ps-bulk-tab-sales_order"
              onClick={() => setBulkForm(f => ({ ...f, source_type: "sales_order", export_order_id: "", product_code: "", item_id: "", excess_entry_id: "" }))}
              className={`px-3 py-1.5 rounded-sm text-xs font-semibold tracking-wide transition-colors ${bulkForm.source_type === "sales_order" ? "bg-[var(--accent)] text-white" : "text-zinc-400 hover:bg-[var(--bg-1)]"}`}
            >SO</button>
            )}
            {!isDomesticMode && (
            <button type="button" data-testid="ps-bulk-tab-export_order"
              onClick={() => setBulkForm(f => ({ ...f, source_type: "export_order", order_id: "", product_code: "", item_id: "", excess_entry_id: "" }))}
              className={`px-3 py-1.5 rounded-sm text-xs font-semibold tracking-wide transition-colors ${bulkForm.source_type === "export_order" ? "bg-sky-500 text-white" : "text-zinc-400 hover:bg-[var(--bg-1)]"}`}
            >ESO</button>
            )}
            {!isExportMode && (
            <button type="button" data-testid="ps-bulk-tab-excess_so"
              onClick={() => setBulkForm(f => ({ ...f, source_type: "excess_so", export_order_id: "", product_code: "", item_id: "", excess_entry_id: "" }))}
              title="Pack against Approved-for-Billing Over-Production entries"
              className={`px-3 py-1.5 rounded-sm text-xs font-semibold tracking-wide transition-colors ${bulkForm.source_type === "excess_so" ? "bg-amber-500 text-black" : "text-amber-300 hover:bg-amber-500/10 border border-amber-500/40"}`}
            >Excess vs SO</button>
            )}
            {!isDomesticMode && (
            <button type="button" data-testid="ps-bulk-tab-excess_eso"
              onClick={() => setBulkForm(f => ({ ...f, source_type: "excess_eso", order_id: "", product_code: "", item_id: "", excess_entry_id: "" }))}
              title="Pack against Approved-for-Billing Over-Production entries (ESO)"
              className={`px-3 py-1.5 rounded-sm text-xs font-semibold tracking-wide transition-colors ${bulkForm.source_type === "excess_eso" ? "bg-amber-500 text-black" : "text-amber-300 hover:bg-amber-500/10 border border-amber-500/40"}`}
            >Excess vs ESO</button>
            )}
            <button type="button" data-testid="ps-bulk-tab-stock"
              onClick={() => setBulkForm(f => ({ ...f, source_type: "stock", customer_id: "", order_id: "", export_order_id: "", product_code: "", item_id: "", excess_entry_id: "", stock_order_id: "", stock_line_id: "" }))}
              className={`px-3 py-1.5 rounded-sm text-xs font-semibold tracking-wide transition-colors ${bulkForm.source_type === "stock" ? "bg-emerald-500 text-white" : "text-zinc-400 hover:bg-[var(--bg-1)]"}`}
            >Stock</button>
            {(bulkForm.source_type === "excess_so" || bulkForm.source_type === "excess_eso") && (
              <span className="text-[10px] text-amber-300 ml-2 italic">
                Total-Qty gate bypassed · every slip tagged as Excess Dispatch in the ledger
              </span>
            )}
          </div>

          {/* Feb 27, 2026 · Approved excess-lot picker for the two Excess
              tabs. Loads live from `/excess-production/approved-lots` and
              caps per-box qty in the payload builder. */}
          {(bulkForm.source_type === "excess_so" || bulkForm.source_type === "excess_eso") && (
            <div className="mb-3 border border-amber-500/40 rounded-sm bg-amber-500/5 p-3" data-testid="ps-bulk-excess-picker">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] font-mono uppercase tracking-widest text-amber-300">Approved for Billing · {bulkForm.source_type === "excess_so" ? "Sales Order" : "Export Sales Order"} · Pickup Source</div>
                {selectedBulkExcessLot && (() => {
                  const cap = Number(selectedBulkExcessLot.excess_qty) || 0;
                  const used = (bulkForm.boxes || []).reduce((s, b) => s + (Number(b.meters) || 0), 0);
                  const rem = Math.max(0, cap - used);
                  return (
                    <div className="text-[10px] font-mono flex items-center gap-2" data-testid="ps-bulk-excess-capacity">
                      <span className="text-zinc-400">Used <span className="text-white">{used.toFixed(2)}</span> / <span className="text-emerald-300">{cap}</span> {selectedBulkExcessLot.unit || ""}</span>
                      <span className={`px-1.5 py-0.5 rounded-sm ${rem <= 0 ? "bg-red-500/20 text-red-300" : "bg-emerald-500/20 text-emerald-300"}`}>{rem.toFixed(2)} remaining</span>
                    </div>
                  );
                })()}
              </div>
              {/* Feb 28, 2026 · Selected-lot summary strip · matches single dialog */}
              {selectedBulkExcessLot && (
                <div className="mb-2 p-2 rounded-sm border border-amber-500/60 bg-[var(--bg-0)]/60" data-testid="ps-bulk-excess-selected-summary">
                  <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-[11px] font-mono">
                    <span className="text-[9px] uppercase tracking-widest text-amber-400">Packing Against</span>
                    <span className="text-white"><span className="text-zinc-500">{bulkForm.source_type === "excess_so" ? "SO" : "ESO"}:</span> <span className="text-amber-300 font-semibold">{selectedBulkExcessLot.order_no || "—"}</span></span>
                    <span className="text-white"><span className="text-zinc-500">Line:</span> <span className="text-zinc-200 truncate max-w-[280px] inline-block align-bottom">{selectedBulkExcessLot.article_no || selectedBulkExcessLot.product_code || "—"}</span></span>
                    {selectedBulkExcessLot.style_no && <span className="text-white"><span className="text-zinc-500">Style:</span> <span className="text-zinc-200">{selectedBulkExcessLot.style_no}</span></span>}
                    <span className="text-white"><span className="text-zinc-500">Fabric:</span> <span className="text-zinc-200">{selectedBulkExcessLot.fabric_type || "—"}</span></span>
                    <span className="text-white"><span className="text-zinc-500">Entry:</span> <span className="text-zinc-200">{selectedBulkExcessLot.entry_no}</span></span>
                    <span className="text-white"><span className="text-zinc-500">Approved Qty:</span> <span className="text-emerald-300">{selectedBulkExcessLot.excess_qty} {selectedBulkExcessLot.unit || ""}</span></span>
                  </div>
                </div>
              )}
              {approvedLotsLoading ? <div className="text-xs text-zinc-500">Loading approved lots…</div>
              : approvedLots.length === 0 ? (
                <div className="text-xs" data-testid="ps-bulk-excess-empty">
                  <div className="text-zinc-400 mb-1.5">No <span className="text-amber-300 font-semibold">Approved-for-Billing</span> Over-Production entries yet.</div>
                  <div className="text-[11px] text-zinc-500 mb-2 leading-relaxed">
                    Excess entries are auto-created when a Packing Slip's meters exceed the SO/ESO line's pending balance. An Admin approves them in the queue — approved lots then appear here for bulk excess dispatch.
                  </div>
                  <a href="/dispatch/excess-production" data-testid="ps-bulk-excess-empty-cta" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/60 text-amber-200 text-[11px] font-semibold">
                    Go to Excess Production Queue
                    {pendingExcessCount > 0 && <span className="px-1.5 py-0.5 rounded-sm bg-amber-500/40 text-white font-mono">{pendingExcessCount} pending</span>}
                    <span aria-hidden="true">→</span>
                  </a>
                </div>
              )
              : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                  {approvedLots.map(l => (
                    <button key={l.id} type="button" data-testid={`ps-bulk-excess-lot-${l.entry_no}`}
                      onClick={() => setBulkForm(f => ({ ...f, excess_entry_id: l.id, order_id: bulkForm.source_type === "excess_so" ? l.order_id : "", export_order_id: bulkForm.source_type === "excess_eso" ? l.order_id : "", product_code: l.product_code || "", style_no: l.style_no || f.style_no, customer_id: l.customer_id || f.customer_id }))}
                      className={`text-left p-2 rounded-sm border text-xs ${bulkForm.excess_entry_id === l.id ? "border-amber-400 bg-amber-500/10" : "border-[var(--border)] hover:border-amber-500/40"}`}>
                      <div className="flex items-center justify-between font-mono">
                        <span className="text-amber-300 font-semibold">{l.entry_no}</span>
                        <span className="text-emerald-300 font-semibold">{l.excess_qty} {l.unit}</span>
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-[10px]">
                        <span className="px-1.5 py-0.5 rounded-sm bg-blue-500/15 border border-blue-500/40 text-blue-300 font-mono font-semibold">
                          {bulkForm.source_type === "excess_so" ? "SO" : "ESO"} · {l.order_no || "—"}
                        </span>
                        {l.style_no && <span className="text-zinc-400">Style <span className="text-zinc-200 font-mono">{l.style_no}</span></span>}
                      </div>
                      <div className="text-[10px] text-zinc-400 truncate mt-0.5" title={l.article_no || l.product_code}>
                        <span className="text-zinc-500">Line: </span>{l.article_no || l.product_code}
                      </div>
                      <div className="text-[9px] text-zinc-500 truncate">{l.fabric_type || "—"} · from slip {l.slip_no}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-6 gap-3">
            {bulkForm.source_type === "stock" && (
              <div className="col-span-6 grid grid-cols-2 gap-3 [&>div]:contents">
                <StockPackPicker prefix="ps-bulk" orders={isExportMode ? exportOrders : orders} isExport={isExportMode} selCls={selCls}
                  orderId={bulkForm.stock_order_id} lineId={bulkForm.stock_line_id}
                  errOrder={errors["bulk.stock_order_id"]} errLine={errors["bulk.stock_line_id"]}
                  onOrder={(v) => setBulkForm(f => ({ ...f, stock_order_id: v, stock_line_id: "", product_code: "", item_id: "" }))}
                  onLine={(v) => setBulkForm(f => {
                    const so = (isExportMode ? exportOrders : orders).find(o => o.id === f.stock_order_id);
                    const l = stockLinesOf(so, isExportMode).find(x => x.line_id === v);
                    return { ...f, stock_line_id: v, product_code: l ? l.code : "", item_id: l ? (l.item_id || l.code) : "", style_no: l?.style_no || f.style_no };
                  })} />
              </div>
            )}
            {(bulkForm.source_type === "export_order" || bulkForm.source_type === "excess_eso") && (
              <>
                <div className="col-span-3">
                  <div className="overline mb-1.5">Buyer (Customer Master)</div>
                  <SearchableCustomerPicker
                    testIdPrefix="ps-bulk-eso-buyer"
                    open={bulkCustomerOpen}
                    setOpen={setBulkCustomerOpen}
                    query={bulkCustomerQuery}
                    setQuery={setBulkCustomerQuery}
                    visible={bulkCustomerVisible}
                    total={customers.length}
                    picked={pickedCustomer}
                    onPick={(c) => setBulkForm(f => ({ ...f, customer_id: c.id, export_order_id: "", product_code: "", item_id: "" }))}
                  />
                </div>
                <div className="col-span-3">
                  <div className="overline mb-1.5">
                    Export Sales Order
                    <span className="text-zinc-500 normal-case ml-2 tracking-normal">
                      {bulkForm.customer_id ? `${bulkExportOrdersForCustomer.length} for this buyer` : `${exportOrders.length} total`}
                    </span>
                  </div>
                  <Select
                    value={bulkForm.export_order_id}
                    onValueChange={v => setBulkForm(f => ({ ...f, export_order_id: v, product_code: "", item_id: "" }))}
                  >
                    <SelectTrigger data-testid="ps-bulk-eso" className={selCls}><SelectValue placeholder={bulkForm.customer_id ? "Pick an Export SO…" : "Pick Buyer first (or leave blank for any ESO)"} /></SelectTrigger>
                    <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                      {bulkExportOrdersForCustomer.map(o => <SelectItem key={o.id} value={o.id} data-testid={`ps-bulk-eso-opt-${o.doc_no}`}>{o.doc_no}{isStockOrder(o) ? " · STOCK" : ""} — {o.buyer_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            {(bulkForm.source_type === "sales_order" || bulkForm.source_type === "excess_so") && (
              <>
                <div className="col-span-3">
                  <div className="overline mb-1.5">Customer</div>
                  <SearchableCustomerPicker
                    testIdPrefix="ps-bulk-customer"
                    open={bulkCustomerOpen}
                    setOpen={setBulkCustomerOpen}
                    query={bulkCustomerQuery}
                    setQuery={setBulkCustomerQuery}
                    visible={bulkCustomerVisible}
                    total={customers.length}
                    picked={pickedCustomer}
                    onPick={(c) => setBulkForm(f => ({ ...f, customer_id: c.id, order_id: "", product_code: "", item_id: "" }))}
                  />
                </div>
                <div className="col-span-3">
                  <div className="overline mb-1.5">
                    Sales Order
                    <span className="text-zinc-500 normal-case ml-2 tracking-normal">
                      {bulkForm.customer_id ? `${bulkOrdersForCustomer.length} for this customer` : `${orders.length} total`}
                    </span>
                  </div>
                  <Select
                    value={bulkForm.order_id}
                    onValueChange={v => setBulkForm(f => ({ ...f, order_id: v, product_code: "", item_id: "" }))}
                  >
                    <SelectTrigger data-testid="ps-bulk-order" className={selCls}><SelectValue placeholder={bulkForm.customer_id ? "Pick a Sales Order…" : "Pick Customer first (or leave blank for any SO)"} /></SelectTrigger>
                    <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                      {bulkOrdersForCustomer.map(o => <SelectItem key={o.id} value={o.id} data-testid={`ps-bulk-order-opt-${o.order_no}`}>{o.order_no}{isStockOrder(o) ? " · STOCK" : ""} — {customerNameFor(o.id) || "—"}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div className="col-span-6">
              <div className="overline mb-1.5 flex items-center gap-3">
                <span>Article / Product Code</span>
                <span className="text-zinc-500 normal-case tracking-normal" data-testid="ps-bulk-product-count">
                  {(bulkForm.style_no || "").trim()
                    ? `${bulkFilteredItems.length}/${bulkAllowedItems.length} match style`
                    : `${bulkAllowedItems.length} available`}
                </span>
                {/* Aug 2026 · Real-time Pending Balance badge — only when
                    an order (SO/ESO) AND a product_code are chosen. */}
                {bulkForm.product_code && (bulkForm.order_id || bulkForm.export_order_id) && (
                  <span
                    data-testid="ps-bulk-pending-balance"
                    className={`ml-auto px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${
                      pendingBalance.loading
                        ? "text-zinc-400 border-zinc-500/40 bg-zinc-500/5"
                        : pendingBalance.pending_qty <= 0
                          ? "text-red-300 border-red-500/40 bg-red-500/10"
                          : bulkTotals.meters > pendingBalance.pending_qty
                            ? "text-red-300 border-red-500/40 bg-red-500/10"
                            : "text-emerald-300 border-emerald-500/40 bg-emerald-500/10"
                    }`}
                  >
                    {pendingBalance.loading ? "computing…" :
                      `Pending: ${pendingBalance.pending_qty.toLocaleString("en-IN",{ minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${pendingBalance.unit}` +
                      (pendingBalance.packed_qty > 0 ? ` · Packed ${pendingBalance.packed_qty.toLocaleString("en-IN",{ minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "") +
                      ` / Ordered ${pendingBalance.order_qty.toLocaleString("en-IN",{ minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  </span>
                )}
              </div>
              {/* Searchable product-code picker (Feb 2026 · user ask). Native
                  button opens a dropdown with a live-filter <input> on top so
                  the operator can narrow by article code OR style substring —
                  Select from shadcn doesn't ship with a built-in search box. */}
              <div className="relative" data-nouppercase>
                <button
                  type="button"
                  data-testid="ps-bulk-product"
                  onClick={() => setBulkProductOpen(v => !v)}
                  className={`${selCls} w-full px-3 text-left flex items-center justify-between`}
                >
                  <span className={bulkForm.product_code ? "text-white font-mono truncate" : "text-zinc-500"}>
                    {bulkForm.product_code || ((bulkForm.style_no || "").trim() && bulkFilteredItems.length === 0
                      ? "No articles match this style"
                      : "Pick Article / Product Code…")}
                  </span>
                  <span className="text-zinc-500 text-xs">▾</span>
                </button>
                {bulkProductOpen && (
                  <div className="absolute z-50 mt-1 w-full bg-[var(--bg-1)] border border-[var(--border)] rounded-sm shadow-xl">
                    <div className="p-2 border-b border-[var(--border)]">
                      <input
                        autoFocus
                        data-testid="ps-bulk-product-search"
                        data-nouppercase
                        value={bulkProductQuery}
                        onChange={e => setBulkProductQuery(e.target.value)}
                        className={inp}
                        placeholder="Search by code or style… "
                      />
                      <div className="text-[10px] text-zinc-500 mt-1">
                        {bulkProductVisible.length} of {bulkFilteredItems.length} shown
                      </div>
                    </div>
                    <div className="max-h-56 overflow-y-auto py-1">
                      {bulkProductVisible.length === 0 && (
                        <div className="px-3 py-3 text-[11px] text-amber-400" data-testid="ps-bulk-product-empty">
                          No articles match "<span className="font-mono">{bulkProductQuery || bulkForm.style_no}</span>"
                        </div>
                      )}
                      {bulkProductVisible.map(it => (
                        <button
                          type="button"
                          key={it.id}
                          data-testid={`ps-bulk-product-opt-${it.code}`}
                          onClick={() => {
                            setBulkForm(f => ({
                              ...f,
                              item_id: it.id,
                              product_code: it.code,
                              style_no: f.style_no || it.style_no || "",
                            }));
                            setBulkProductOpen(false);
                            setBulkProductQuery("");
                          }}
                          className="w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--bg-2)] flex items-center justify-between gap-3"
                        >
                          <span className="font-mono text-white truncate">{it.code}</span>
                          {it.style_no && <span className="text-zinc-500 text-[10px] font-mono shrink-0">{it.style_no}</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="col-span-3">
              <div className="overline mb-1.5">
                Style Number
                <span className="text-zinc-500 normal-case ml-2 tracking-normal">· search</span>
              </div>
              {/* Searchable style-number typeahead (Feb 2026 · user ask). Free
                  text is preserved (buyer-supplied styles that don't yet live
                  in Article Master), but the moment matches exist we surface
                  them so the operator can lock onto an existing style with
                  one click. */}
              <div className="relative" data-nouppercase>
                <input
                  data-testid="ps-bulk-style-no"
                  value={bulkForm.style_no}
                  onFocus={() => setBulkStyleOpen(true)}
                  onBlur={() => setTimeout(() => setBulkStyleOpen(false), 180)}
                  onChange={e => setBulkForm(f => {
                    const nextStyle = e.target.value.toUpperCase();
                    const stillMatches = !nextStyle.trim() || bulkAllowedItems.some(it =>
                      it.id === f.item_id && (it.style_no || "").toUpperCase().includes(nextStyle)
                    );
                    return {
                      ...f,
                      style_no: nextStyle,
                      ...(stillMatches ? {} : { item_id: "", product_code: "" }),
                    };
                  })}
                  className={inp}
                  placeholder="Type style to search · e.g. WB0012"
                  style={{ textTransform: "uppercase" }}
                />
                {bulkStyleOpen && bulkStyleSuggestions.length > 0 && (
                  <div className="absolute z-50 mt-1 w-full bg-[var(--bg-1)] border border-[var(--border)] rounded-sm shadow-xl max-h-56 overflow-y-auto py-1">
                    <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
                      {bulkStyleSuggestions.length} match{bulkStyleSuggestions.length === 1 ? "" : "es"}
                    </div>
                    {bulkStyleSuggestions.map(s => (
                      <button
                        type="button"
                        key={s}
                        data-testid={`ps-bulk-style-opt-${s}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setBulkForm(f => ({ ...f, style_no: s }));
                          setBulkStyleOpen(false);
                        }}
                        className="w-full text-left px-3 py-1.5 text-xs font-mono text-white hover:bg-[var(--bg-2)]"
                      >{s}</button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="col-span-3">
              <div className="overline mb-1.5">Pack Date</div>
              <input
                type="date"
                data-testid="ps-bulk-date"
                data-err-key="bulk.pack_date"
                value={bulkForm.pack_date}
                onChange={e => setBulkForm(f => ({ ...f, pack_date: e.target.value }))}
                className={inp + errClass("bulk.pack_date")}
              />
            </div>
            <div className="col-span-3"></div>
          </div>

          {/* Feb 2026 · Multi-line Per-Box Profile (was single-profile × N).
              One line = one Packing Slip. Meters / Joints / Net / Tare can
              vary per row so mixed-size shipments key in a single dialog. */}
          <div className="mt-3 border border-[var(--border)] rounded-sm p-3 bg-[var(--bg-2)]/40">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="overline text-zinc-400">Per-Box Profile · applied to every slip</div>
                <div className="text-[10px] text-zinc-500 mt-0.5" data-testid="ps-bulk-line-count">
                  {(bulkForm.boxes || []).length} slip{(bulkForm.boxes || []).length === 1 ? "" : "s"} will be generated · one per line
                </div>
              </div>
              <button
                type="button"
                data-testid="ps-bulk-add-line"
                onClick={addBulkBox}
                disabled={(bulkForm.boxes || []).length >= 200}
                className="h-8 px-3 border border-[var(--border)] hover:bg-[var(--bg-1)] disabled:opacity-40 text-[11px] font-medium tracking-wide rounded-sm text-zinc-300 flex items-center gap-1"
              ><PlusIcon size={12} weight="bold" />Add Line</button>
            </div>
            <div className="border border-[var(--border)] rounded-sm overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-[var(--bg-1)]/60">
                  <tr className="text-[10px] uppercase tracking-widest text-zinc-500">
                    <th className="text-left px-2 py-2 w-10">#</th>
                    <th className="text-right px-2 py-2">Meters</th>
                    <th className="text-right px-2 py-2">Joints</th>
                    <th className="text-right px-2 py-2">Net Wt (kg)</th>
                    <th className="text-right px-2 py-2">Tare Wt (kg)</th>
                    <th className="text-right px-2 py-2 bg-emerald-500/10">Gross Wt (kg)</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody data-testid="ps-bulk-boxes">
                  {(bulkForm.boxes || []).map((r, i) => {
                    const gross = (Number(r.net_weight) || 0) + (Number(r.tare_weight) || 0);
                    // Aug 2026 · Per-row red validation — a single box or
                    // the running row-total exceeds Pending Balance.
                    const rowMeters = Number(r.meters) || 0;
                    const showBalance = !!bulkForm.product_code && (!!bulkForm.order_id || !!bulkForm.export_order_id);
                    const singleOver = showBalance && pendingBalance.pending_qty > 0 && rowMeters > pendingBalance.pending_qty;
                    const cumulativeOver = showBalance && bulkTotals.meters > pendingBalance.pending_qty && rowMeters > 0;
                    const meterErr = singleOver || cumulativeOver;
                    return (
                      <tr key={i} className={`border-t border-[var(--border)] ${meterErr ? "bg-red-500/5" : ""}`}>
                        <td className="px-2 py-1 font-mono text-zinc-500">{String(i + 1).padStart(2, "0")}</td>
                        <td className="p-1">
                          <FormattedNumberInput
                            step="0.01"
                            data-testid={`ps-bulk-meters-${i}`}
                            data-err-key={`bulk.box.${i}.meters`}
                            value={r.meters}
                            onChange={e => setBulkBox(i, "meters", e.target.value)}
                            className={inpNum + " h-8 text-right font-mono " + (meterErr ? "border-red-500/70 text-red-300" : "") + errClass(`bulk.box.${i}.meters`)}
                          />
                          {meterErr && (
                            <div className="text-[9px] font-mono text-red-400 mt-0.5 text-right whitespace-nowrap" data-testid={`ps-bulk-meters-err-${i}`}>
                              Exceeds Pending {pendingBalance.pending_qty.toFixed(2)} {pendingBalance.unit}
                            </div>
                          )}
                        </td>
                        <td className="p-1"><FormattedNumberInput data-testid={`ps-bulk-joints-${i}`} data-err-key={`bulk.box.${i}.joints`} value={r.joints} onChange={e => setBulkBox(i, "joints", e.target.value)} className={inpNum + " h-8 text-right font-mono" + errClass(`bulk.box.${i}.joints`)} /></td>
                        <td className="p-1"><FormattedNumberInput step="0.01" data-testid={`ps-bulk-net-${i}`} data-err-key={`bulk.box.${i}.net_weight`} value={r.net_weight} onChange={e => setBulkBox(i, "net_weight", e.target.value)} className={inpNum + " h-8 text-right font-mono" + errClass(`bulk.box.${i}.net_weight`)} /></td>
                        <td className="p-1"><FormattedNumberInput step="0.01" data-testid={`ps-bulk-tare-${i}`} data-err-key={`bulk.box.${i}.tare_weight`} value={r.tare_weight} onChange={e => setBulkBox(i, "tare_weight", e.target.value)} className={inpNum + " h-8 text-right font-mono" + errClass(`bulk.box.${i}.tare_weight`)} /></td>
                        <td className="px-2 py-1 text-right font-mono text-emerald-400 bg-emerald-500/5" data-testid={`ps-bulk-gross-${i}`}>{gross.toFixed(2)}</td>
                        <td className="px-1 py-1 text-center">
                          {(bulkForm.boxes || []).length > 1 && (
                            <button type="button" data-testid={`ps-bulk-rm-${i}`} onClick={() => rmBulkBox(i)} className="h-7 w-7 inline-flex items-center justify-center text-zinc-500 hover:text-red-400" title="Remove line">
                              <TrashIcon size={12} weight="bold" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t border-[var(--border)] bg-[var(--bg-1)]/70 font-medium">
                    <td className="px-2 py-2 text-right text-[10px] uppercase tracking-widest text-zinc-500" colSpan={1}>Total</td>
                    <td className="px-2 py-2 text-right font-mono text-white" data-testid="ps-bulk-total-meters">{bulkTotals.meters.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="px-2 py-2 text-right font-mono text-white" data-testid="ps-bulk-total-joints">{bulkTotals.joints}</td>
                    <td className="px-2 py-2 text-right font-mono text-white" data-testid="ps-bulk-total-net">{bulkTotals.net.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="px-2 py-2 text-right font-mono text-white" data-testid="ps-bulk-total-tare">{bulkTotals.tare.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="px-2 py-2 text-right font-mono text-emerald-400 bg-emerald-500/10" data-testid="ps-bulk-total-gross">{bulkTotals.gross.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div className="mt-3">
            <div className="overline mb-1.5">Remarks (shared)</div>
            <input
              data-testid="ps-bulk-remarks"
              value={bulkForm.remarks}
              onChange={e => setBulkForm(f => ({ ...f, remarks: e.target.value }))}
              className={inp}
              placeholder="Optional — e.g. Batch 05 / Shift-A"
            />
          </div>

          <DialogFooter>
            <button onClick={() => setBulkOpen(false)} className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400">Cancel</button>
            {/* Mar 2026 · Bulk Save Draft — skips validation, stamps status Draft on each created slip. */}
            <button data-testid="ps-bulk-save-draft" onClick={() => saveBulk(true)} disabled={bulkSaving} className="px-4 py-2 rounded-sm border border-amber-500/60 text-sm text-amber-300 hover:bg-amber-500/10 font-semibold disabled:opacity-60">Save Draft</button>
            <button
              onClick={() => saveBulk(false)}
              disabled={bulkSaving}
              data-testid="ps-bulk-save"
              className="px-4 py-2 rounded-sm bg-emerald-500 hover:bg-emerald-600 text-sm font-semibold text-white disabled:opacity-60 flex items-center gap-2"
            >
              <StackIcon size={14} weight="bold" />
              {bulkSaving ? "Creating…" : `Create ${Math.max(1, Math.min(200, (bulkForm.boxes || []).length))} Packing Slips`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert-to-SO mini-picker (Feb 2026) — stock-mode slip re-link. */}
      <Dialog open={!!convertTarget} onOpenChange={(v) => { if (!v) closeConvert(); }}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-md" data-testid="ps-convert-dialog" description="Packing / dispatch document action — Packing Slip, Delivery Challan or Packing List.">
          <DialogHeader><DialogTitle className="font-display">Convert to SO Slip · {convertTarget?.slip_no}</DialogTitle></DialogHeader>
          <div className="text-xs text-zinc-400 mb-3">
            Pick a Sales Order to attach this stock slip to. The slip's Product Code (
            <span className="font-mono text-zinc-300">{((convertTarget?.boxes && convertTarget.boxes[0]) || {}).product_code || "—"}</span>
            ) must be one of the SO's line items.
          </div>
          <div className="overline mb-1.5">Sales Order</div>
          <Select value={convertSoId} onValueChange={setConvertSoId}>
            <SelectTrigger data-testid="ps-convert-so" className={selCls}><SelectValue placeholder="Pick a Sales Order…" /></SelectTrigger>
            <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
              {orders.map(o => <SelectItem key={o.id} value={o.id}>{o.order_no}</SelectItem>)}
            </SelectContent>
          </Select>
          <DialogFooter className="mt-4">
            <button onClick={closeConvert} className="px-4 py-2 rounded-sm bg-[var(--bg-2)] text-sm">Cancel</button>
            <button onClick={convertToSO} disabled={!convertSoId} data-testid="ps-convert-confirm" className="px-4 py-2 rounded-sm bg-[var(--accent)] text-white text-sm font-semibold disabled:opacity-50">Convert &amp; Link</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

